import React, { useState, useEffect, useRef, useContext } from 'react'
import { useNavigate, Link, useParams, useSearchParams } from 'react-router-dom'
import { ChevronDown, ChevronUp } from 'lucide-react'
import Colors from '../../Collections/RegisterCollection/collapse/Colors'
import Measures, { MeasuresHandle } from '../../Collections/RegisterCollection/collapse/Measures'
import BaseMaterials from '../../Collections/RegisterCollection/collapse/BaseMaterials'
import Beneficiamento, {
  BeneficiamentoNota,
  Sector as BeneficiamentoSector,
  newBeneficiamentoNota,
} from '../../Collections/RegisterCollection/collapse/Beneficiamento'
import PreCost from '../../Collections/RegisterCollection/collapse/PreCost'
import Aprovations from '../../Collections/RegisterCollection/collapse/Aprovations'
import axios from '../../../../config/axios'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import InfiniteSelect from '../../../../components/InfiniteSelect'
import { ExistingAttachment } from '../../../../components/AttachmentManager'
import ProductAttachmentsAside, {
  isAllowedDocumentUpload,
  isProductImageFile,
} from './AttachmentsAside'
import {
  SubHeaderContainer,
  SectionName,
  LeftSide,
} from '../../../../components/styles/subheader-style'
import {
  MainContainer,
  StyledCard,
  FormGrid,
  StyledFormGroup,
  ActionButtons,
  CancelButton,
  SaveButton,
  BreadcrumbsContainer,
  CardTitle,
  GraduationGrid,
  GraduationButton,
  CompositionTableWrapper,
  CompositionTable,
  CompositionTableHeader,
  CompositionTableRow,
  TableCell,
  TableSelect,
  TableInput,
  DeleteButton,
  AddItemButton,
  ImportButton,
  TagsContainer,
  Tag,
  RemoveTagButton,
  SelectContainer,
  PageGrid,
  FormColumn,
  AsideColumn,
} from './style'
import { CollapsePanelHeader } from '../../Collections/RegisterCollection/style'
import CurrencyInput from '../../../../components/CurrencyInput'
import { downloadRemoteFile } from '../../../../utils/downloadRemoteFile'
import trash from '../../../../assets/equipment/Tables/trash.svg'
import * as CollectionStyles from '../../Collections/RegisterCollection/style'
import plus from '../../../../assets/certificates/AddParameter/blue-plus.svg'
import save from '../../../../assets/certificates/AddParameter/save.svg'
import cross from '../../../../assets/addStockModal/cross.svg'

const GATEWAY_URL = process.env.REACT_APP_GATEWAY_BASE_URL
const FASHION_API_URL = `${GATEWAY_URL}/backend-fashion-orders/fashion-product`

interface Collection {
  id: number
  name: string
}

interface CollectionEpisodeOption {
  id: number
  name: string
}

interface Family {
  id: number
  name: string
  collectionId: number
}

interface Group {
  id: number
  name: string
}



interface GradeType {
  id: number
  name: string
}

interface Grade {
  id: number
  name: string
  typeId: number
  type: GradeType
  GraduationType?: GradeType
}

interface Stock {
  id: number
  description: string
  costPerKg?: number
  costPerMm?: number
}

interface ResourceCategory {
  id: number
  name: string
}

interface Resource {
  id: number
  name: string
  description: string
  categoryId: number
  materialId?: number | null
  prices?: ResourcePrice[]
}

interface ResourcePrice {
  id: number
  price: number
  unitOfMeasurement?: {
    id: number
    name: string
    abbreviation: string
  }
}

interface StageService {
  id: number
  name: string
  category?: { id: number; name: string } | null
  type?: { id: number; name: string } | null
  code?: string
  deadlineInDays?: number
}

interface MaterialVariation {
  id: number
  colorId: number | null
  stockId: number | null
  resourceId: number | null
  color: { id: number; name: string } | null
  stock: Stock | null
  resource: Resource | null
}

interface ColorVariationMapping {
  mainVariationId: number
  mainColorName: string
  itemVariationId: number | null
}

function scriptCostPerUnitToInput(value: unknown): string {
  if (value === undefined || value === null) return ''
  const s = String(value).trim()
  if (!s || s === 'null' || s === 'undefined') return ''
  return s
}

/** Apenas dígitos e no máximo um separador decimal (`,` ou `.`). */
function sanitizeCompositionDecimalInput(raw: string): string {
  if (!raw) return ''
  const normalized = raw.replace(/,/g, '.')
  let result = ''
  let hasDot = false
  for (const ch of normalized) {
    if (ch >= '0' && ch <= '9') {
      result += ch
    } else if (ch === '.' && !hasDot) {
      hasDot = true
      result += '.'
    }
  }
  return result
}

const getStockFinishing = (stock: any): string =>
  String(stock?.Finishing?.type ?? stock?.finishing ?? '').trim()

interface CompositionItem {
  id: number
  tipo: 'materia-prima' | 'insumo' | 'servico' | ''
  materialId?: number | null
  stockId: number | null
  resourceId: number | null
  stageId?: number | null
  categoryId: number | null
  descricao: string
  unidadeMedida: string
  unitOfMeasurementId?: number | null
  localEstoque: string
  consumo: string
  costPerUnit?: string
  finishing?: string | null
  availableVariations: MaterialVariation[]
  autoCreated?: boolean
  productItemColors?: Array<{
    id?: number
    collectionColorId: number
    itemColorId?: number | null
    itemColorName?: string | null
    resourceId?: number | null
    stockId?: number | null
  }>
  resourceItemColorId?: number | null
  resourceItemColorName?: string | null
  attachments?: string[]
  serviceFiles?: File[]
}

/** Igual ao `value` do `<TableSelect>` de insumo — usado para evitar ocupar todas as variantes do mesmo material. */
function insumoOptionValueFromItem(ci: CompositionItem): string | null {
  if (ci.tipo !== 'insumo') return null
  if (ci.resourceId) return `r:${ci.resourceId}`
  if (ci.materialId != null) return `${ci.materialId}:${ci.categoryId ?? ''}`
  return null
}

function insumoOptionValueFromGroup(g: {
  resourceId?: number | null
  materialId?: number | null
  categoryId?: number | null
}): string | null {
  if (g.resourceId != null) return `r:${g.resourceId}`
  if (g.materialId != null) return `${g.materialId}:${g.categoryId ?? ''}`
  return null
}

function materiaPrimaFamilyMaterialIdFromItem(
  ci: CompositionItem,
  conjuntos: Array<{ familyMaterialId: number; stockId: number | null; materialId: number }>,
): number | null {
  if (ci.tipo !== 'materia-prima') return null
  const match = conjuntos.find(
    (c) =>
      (ci.stockId && c.stockId === ci.stockId) ||
      (!ci.stockId && ci.materialId != null && c.materialId === ci.materialId),
  )
  return match?.familyMaterialId ?? null
}

function servicoStageIdFromItem(ci: CompositionItem): number | null {
  if (ci.tipo !== 'servico' || ci.stageId == null) return null
  return ci.stageId
}

interface ProductItemColorEntry {
  id?: number
  collectionColorId: number
  itemColorId?: number | null
  itemColorName?: string | null
  materialId?: number | null
  stockId?: number | null
  resourceId?: number | null
}

interface ProductData {
  id?: number
  productCode: string
  name: string
  description?: string
  referenceProduct?: string
  observations?: string
  attachments?: string[]
  CoverGeneralFile?: { fileUrl: string } | null
  collectionId: number
  familyId: number
  groupId: number
  gradeInProduct?: Array<{
    id: number
    productId: number
    gradeId: number
    grade: Grade
  }>
  productItemColors?: ProductItemColorEntry[]
  composition?: {
    id: number
    fashionProductId: number
    variations: Array<{
      id: number
      compositionId: number
      stockId: number | null
      resourceId: number | null
      description: string
      stockLocation: string
      usage: string
      stock: Stock | null
      resource: Resource | null
      colorMappings: Array<{
        id: number
        fashionProductVariationId: number
        mainMaterialVariationId: number
        itemVariationId: number
        mainMaterialVariation?: MaterialVariation
        itemVariation?: MaterialVariation
      }>
    }>
  }
  compositionItems?: {
    stockId: number | null
    resourceId: number | null
    description: string
    stockLocation: string
    usage: number
    colorMappings: Array<{
      mainMaterialVariationId: number
      itemVariationId: number
    }>
  }[]

  Script?: Array<{
    id: number
    name: string
    productId: number
    ScriptMaterial?: Array<{
      id: number
      scriptId: number
      materialId: number
      stockId?: number | null
      idealMeasurements: number | null
      weight: number | null
      costPerUnit?: string | number | null
      step: number
      Material: {
        id: number
        name: string
        density: number
        tenantId: number
        Stock: Array<{
          id: number
          description: string
          costPerKg?: number
          costPerMm?: number
          StockLocation?: {
            id: number
            name: string
            tenantId: number
          }
          StockColors: Array<{
            id: number
            stockId: number
            colorId: number
            ItemColor: {
              id: number
              name: string
              tenantId: number
            }
          }>
        }>
      }
      Stock?: {
        id: number
        codeId?: number | string | null
        description?: string | null
        materialId?: number | null
        Material?: { id: number; name?: string | null } | null
      } | null
    }>
    ScriptResource?: Array<{
      id: number
      scriptId: number
      resourceId: number
      stockId?: number | null
      weight: number | null
      costPerUnit?: string | number | null
      step: number
      unitOfMeasurementId: number | null
      Resource: Resource & {
        Category_Resource?: {
          id: number
          name: string
          tenantId: number
        }
        ResourcePrice: ResourcePrice[]
      }
      Stock?: {
        id: number
        codeId?: number | string | null
        description?: string | null
        materialId?: number | null
        Material?: { id: number; name?: string | null } | null
      } | null
      UnitOfMeasurement?: {
        id: number
        name: string
        abbreviation: string
      }
    }>
    ScriptStage?: Array<any>
  }>
  Collection?: {
    id: number
    name: string
    tenantId: number
  }
  Family?: {
    id: number
    name: string
    collectionId: number
    tenantId: number
    Collection?: {
      id: number
      name: string
      tenantId: number
    }
  }
  Group?: {
    id: number
    name: string
    tenantId: number
  }
}

interface FamilyConjunto {
  familyMaterialId: number
  collectionMaterialId: number
  materialId: number
  materialName: string
  stockId: number | null
  stockLabel: string | null
  finishing?: string | null
  linkedResourceMaterials: Array<{
    resourceId?: number | null
    resourceName?: string | null
    materialId: number
    materialName: string
    categoryId?: number | null
    categoryName?: string | null
  }>
}

interface FamilyTrimInsumoOption {
  familyMaterialId: number
  collectionMaterialId: number
  resourceId: number | null
  resourceName: string | null
  materialId: number
  materialName: string
  categoryId?: number | null
  categoryName?: string | null
  finishing?: string
}

interface StockColor {
  id: number
  description: string
  costPerKg?: number
  costPerMm?: number
  StockColors?: Array<{
    id: number
    stockId: number
    colorId: number
    ItemColor: {
      id: number
      name: string
      tenantId: number | null
    }
  }>
}

const Breadcrumbs: React.FC<{ items: { label: string; path?: string }[] }> = ({
  items,
}) => (
  <BreadcrumbsContainer>
    <ol>
      {items.map((item, index) => (
        <li key={index}>
          {index === items.length - 1 ? (
            <span>{item.label}</span>
          ) : item.path ? (
            <Link to={item.path}>{item.label}</Link>
          ) : (
            <span>{item.label}</span>
          )}
        </li>
      ))}
    </ol>
  </BreadcrumbsContainer>
)

const DownloadIcon = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M4.66675 6.66669L8.00008 10L11.3334 6.66669'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M8 10V2'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const RegisterComposition: React.FC = () => {
  const navigate = useNavigate()
  const { productId } = useParams<{ productId?: string }>()
  const isEditMode = !!productId
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')

  const { setFeedback } = useContext(FeedbackContext)

  const initializationCompletedRef = useRef(false)
  const isInitialLoadRef = useRef(true)
  const initialEditFamilyIdRef = useRef<number | null>(null)

  const [loading, setLoading] = useState<boolean>(false)
  const [loadingData, setLoadingData] = useState<boolean>(true)
  const [isInitializing, setIsInitializing] = useState<boolean>(false)
  const [isGeralOpen, setIsGeralOpen] = useState<boolean>(true)
  const [isComposicaoOpen, setIsComposicaoOpen] = useState<boolean>(false)

  const [formData, setFormData] = useState({
    codigo: '',
    nomeProduto: '',
    description: '',
    referenceProduct: '',
    observations: '',
    collectionId: 0,
    familyId: 0,
    groupId: 0,
    episodeId: 0,
    price: 0,
  })

  const [selectedStockColorIds, setSelectedStockColorIds] = useState<number[]>([])

  const [selectedGradeType, setSelectedGradeType] = useState<number | null>(null)

  const [allCollections, setAllCollections] = useState<Collection[]>([])
  const [filteredFamilies, setFilteredFamilies] = useState<Family[]>([])
  const [filteredGroups, setFilteredGroups] = useState<Group[]>([])
  const [collectionEpisodes, setCollectionEpisodes] = useState<CollectionEpisodeOption[]>([])
  const measuresRef = useRef<MeasuresHandle>(null)

  const [gradesByType, setGradesByType] = useState<Record<string, Grade[]>>({})
  const [gradeTypes, setGradeTypes] = useState<GradeType[]>([])
  const [availableGradeIds, setAvailableGradeIds] = useState<number[]>([])
  const [selectedGradeIds, setSelectedGradeIds] = useState<number[]>([])

  const [productSelectedFiles, setProductSelectedFiles] = useState<File[]>([])
  const [productAttachmentUrls, setProductAttachmentUrls] = useState<string[]>([])
  const [productExistingAttachments, setProductExistingAttachments] = useState<ExistingAttachment[]>([])

  const ATTACHMENTS_ASIDE_STORAGE_KEY = 'productCompositionAttachmentsAsideOpen'
  const [isAttachmentsAsideOpen, setIsAttachmentsAsideOpen] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(ATTACHMENTS_ASIDE_STORAGE_KEY)
      if (stored === '0') return false
      if (stored === '1') return true
    } catch (error) {
      console.warn('[RegisterComposition] não foi possível ler estado do aside de anexos:', error)
    }
    return true
  })

  useEffect(() => {
    try {
      localStorage.setItem(ATTACHMENTS_ASIDE_STORAGE_KEY, isAttachmentsAsideOpen ? '1' : '0')
    } catch (error) {
      console.warn('[RegisterComposition] não foi possível salvar estado do aside de anexos:', error)
    }
  }, [isAttachmentsAsideOpen])

  const [allResourceCategories, setAllResourceCategories] = useState<ResourceCategory[]>([])
  const [resourcesByCategory, setResourcesByCategory] = useState<Record<number, Resource[]>>({})
  const [familyConjuntos, setFamilyConjuntos] = useState<FamilyConjunto[]>([])
  const [familyTrimInsumoOptions, setFamilyTrimInsumoOptions] = useState<FamilyTrimInsumoOption[]>([])
  const [conjuntoStocks, setConjuntoStocks] = useState<StockColor[]>([])
  const [allServices, setAllServices] = useState<StageService[]>([])

  const [compositionItems, setCompositionItems] = useState<CompositionItem[]>([])

  const [beneficiamentoNotes, setBeneficiamentoNotes] = useState<BeneficiamentoNota[]>([])
  const [sectors, setSectors] = useState<BeneficiamentoSector[]>([])
  const [isBeneficiamentoOpen, setIsBeneficiamentoOpen] = useState(false)

  const [rcDebugLogs, setRcDebugLogs] = useState<string[]>([])
  const rcDebug = (msg: string) => {
    console.log(msg)
    setRcDebugLogs(prev => [...prev, msg])
  }
  const [units, setUnits] = useState<Array<{ id: number; name: string }>>([])

  const [productData, setProductData] = useState<ProductData | null>(null)

  const uploadProductAttachmentFiles = async (files: File[]) => {
    if (!files || files.length === 0) return [] as string[];
    const token = localStorage.getItem('token')
    const headers = token ? { Authorization: `Bearer ${token}` } : {}

    const uploadedUrls: string[] = []
    for (const file of files) {
      const formData = new FormData()
      formData.append('file', file)

      try {
        const response = await axios.post(
          `${FASHION_API_URL}/product-attachments/upload`,
          formData,
          {
            headers: {
              ...headers,
            },
          },
        )

        if (response.data && response.data.url) {
          uploadedUrls.push(response.data.url)
        } else {
          throw new Error('URL do arquivo não retornada no upload')
        }
      } catch (err) {
        console.error('Erro ao fazer upload de arquivo de anexo:', err)
        throw err
      }
    }

    return uploadedUrls
  }

  const uploadNoteImage = async (file: File): Promise<string> => {
    const token = localStorage.getItem('token')
    const formData = new FormData()
    formData.append('file', file)
    const response = await axios.post(`${FASHION_API_URL}/notes/upload`, formData, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (response.data?.url) return response.data.url
    throw new Error('URL não retornada no upload de imagem de nota')
  }

  const getNoteSignedUrl = async (filePath: string): Promise<string> => {
    const token = localStorage.getItem('token')
    const response = await axios.get(`${FASHION_API_URL}/notes/signed-url`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { filePath },
    })
    return response.data?.url || filePath
  }

  const resolveNoteImageUrls = async (imageUrls: string[]): Promise<string[]> => {
    if (!imageUrls || imageUrls.length === 0) return []
    return Promise.all(imageUrls.map((url) => getNoteSignedUrl(url).catch(() => url)))
  }

  const normalizeProductAttachment = (attachment: any, fallbackIndex: number): ExistingAttachment => {
    const fileUrl = attachment?.fileUrl || attachment?.filePath || ''
    const originalName =
      attachment?.originalName ||
      attachment?.fileName ||
      fileUrl.split('/').pop() ||
      `anexo-${fallbackIndex}`

    return {
      id: Number(attachment?.id ?? fallbackIndex),
      originalName,
      fileName: attachment?.fileName || originalName,
      filePath: attachment?.filePath || fileUrl,
      fileUrl,
      fileSize: Number(attachment?.fileSize ?? 0),
      mimeType: attachment?.mimeType || '',
      isImage: attachment?.isImage === true ? true : undefined,
      isCover: Boolean(attachment?.isCover),
    }
  }

  const getSignedUrlByFilePath = async (
    filePath: string,
    headers: { Authorization?: string },
    forceDownload?: boolean,
  ): Promise<string | null> => {
    if (!filePath) return null

    try {
      const response = await axios.get(`${FASHION_API_URL}/product-attachments/signed-url`, {
        headers,
        params: {
          filePath,
          ...(forceDownload ? { forceDownload: 'true' } : {}),
        },
      })

      return response.data?.data?.url || response.data?.url || null
    } catch (error) {
      console.error('[RC] erro ao obter signed URL por filePath:', error)
      return null
    }
  }

  const resolveProductAttachmentAccessUrl = async (
    attachment: ExistingAttachment,
    action: 'view' | 'download',
  ): Promise<string | null> => {
    const token = localStorage.getItem('token')
    const headers = token ? { Authorization: `Bearer ${token}` } : {}

    const fallbackSource = attachment.fileUrl || attachment.filePath
    if (!fallbackSource) {
      return null
    }

    const signedByFilePath = await getSignedUrlByFilePath(
      fallbackSource,
      headers,
      action === 'download',
    )
    if (signedByFilePath) {
      return signedByFilePath
    }

    return fallbackSource
  }

  const hydrateSignedPreviewUrls = async (attachments: ExistingAttachment[]) => {
    if (!attachments.length) return

    const previewsToHydrate = attachments.filter((attachment) => {
      const source = attachment.filePath || attachment.fileUrl || ''
      const mimeType = String(attachment.mimeType || '').toLowerCase()
      const fileReference = String(
        attachment.originalName || attachment.fileName || source,
      ).toLowerCase()

      const isImageByType = attachment.isImage === true || mimeType.startsWith('image/')
      const isImageByExtension = /\.(png|jpe?g|gif|bmp|webp|svg|avif|heic|heif|jfif)$/i.test(fileReference)

      return Boolean(source) && (isImageByType || isImageByExtension)
    })

    if (!previewsToHydrate.length) return

    const hydratedEntries = await Promise.all(
      previewsToHydrate.map(async (attachment) => {
        const signedUrl = await resolveProductAttachmentAccessUrl(attachment, 'view')
        return {
          id: attachment.id,
          signedUrl,
        }
      }),
    )

    const hydratedById = new Map<number, string>()
    hydratedEntries.forEach((entry) => {
      if (entry.signedUrl) {
        hydratedById.set(entry.id, entry.signedUrl)
      }
    })

    if (!hydratedById.size) return

    setProductExistingAttachments((prev) =>
      prev.map((attachment) => {
        const signedUrl = hydratedById.get(attachment.id)
        if (!signedUrl) return attachment

        return {
          ...attachment,
          filePath: signedUrl,
          isImage: attachment.isImage === undefined ? true : attachment.isImage,
        }
      }),
    )
  }

  const applyProductAttachmentsState = (attachments: any[]) => {
    const normalizedAttachments = (attachments || []).map((attachment, index) =>
      normalizeProductAttachment(attachment, index + 1),
    )

    setProductExistingAttachments(normalizedAttachments)

    const urls = normalizedAttachments
      .map((attachment) => attachment.fileUrl || attachment.filePath)
      .filter((url): url is string => Boolean(url))

    setProductAttachmentUrls(Array.from(new Set(urls)))

    void hydrateSignedPreviewUrls(normalizedAttachments)

    return normalizedAttachments
  }

  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const token = localStorage.getItem('token')
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const resp = await axios.get(`${GATEWAY_URL}/resources/resource/util/units`, { headers })
        console.debug('[RegisterComposition] unidade fetch response', resp.data)
        if (Array.isArray(resp.data)) {
          const excluded = ['Celsius', 'Faren', 'HB', 'HRB', 'HRC', 'J', 'MPa', 'Newton', 'Tn'];
          const filtered = resp.data.filter((u: any) => {
            const name = String(u.name || '').trim();
            return !excluded.some(e => e.toLowerCase() === name.toLowerCase());
          });
          setUnits(filtered)
          console.debug('[RegisterComposition] unidades gravadas', filtered)
        }
      } catch (err) {
        console.error('Erro ao buscar unidades de medida:', err)
      }
    }
    fetchUnits()
  }, [])
  const [allCollectionColors, setAllCollectionColors] = useState<{ id: number; name: string; hex?: string; pantoneCode?: string }[]>([])
  const [allItemColors, setAllItemColors] = useState<{ id: number; name: string }[]>([])
  const [allVariations, setAllVariations] = useState<any[]>([])

  const [selectedCollectionColors, setSelectedCollectionColors] = useState<{ id: number; name: string; hex?: string; pantoneCode?: string }[]>([])
  const selectedCollectionColorsPrevLength = React.useRef<number>(0)
  const removeCollectionColor = (id: number) => {
    setSelectedCollectionColors(prev => prev.filter(c => Number(c.id) !== Number(id)))
  }



  const insumoMaterialGroups = React.useMemo(() => {
    const seen = new Set<string>()
    const result: Array<{
      resourceId?: number | null
      materialId: number
      materialName: string
      categoryId?: number | null
      categoryName?: string | null
      familyMaterialId?: number
      finishing?: string
    }> = []

    for (const lr of familyTrimInsumoOptions) {
      const hasResource = typeof lr.resourceId === 'number'
      if (!hasResource && !lr.materialId) continue
      const key = hasResource
        ? `r:${lr.resourceId}`
        : `m:${lr.materialId}:${lr.categoryId ?? ''}`
      if (seen.has(key)) continue
      seen.add(key)
      const baseName = lr.resourceName || lr.materialName || ''
      const label = lr.categoryName ? `${baseName} (${lr.categoryName})` : baseName
      result.push({
        resourceId: hasResource ? lr.resourceId : null,
        materialId: lr.materialId,
        materialName: label,
        categoryId: lr.categoryId ?? null,
        categoryName: lr.categoryName ?? null,
        familyMaterialId: lr.familyMaterialId,
        finishing: lr.finishing,
      })
    }

    return result
  }, [familyTrimInsumoOptions])

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoadingData(true)
      const token = localStorage.getItem('token')
      const headers = { Authorization: `Bearer ${token}` }

      try {
        const res = await axios.get(`${FASHION_API_URL}/collections`, { headers })
        const data = Array.isArray(res.data) ? res.data : []
        setAllCollections(data.map((c: any) => ({ id: c.id, name: c.name })))
      } catch (error) {
        console.error('Erro ao carregar collections:', error)
      }

      try {
        const res = await axios.get(`${FASHION_API_URL}/grades`, { headers })
        const grades: Grade[] = Array.isArray(res.data) ? res.data : []

        const typesMap = new Map<number, GradeType>()
        grades.forEach((grade) => {
          const gradeType = grade.GraduationType || grade.type;
          if (gradeType && !typesMap.has(gradeType.id)) {
            typesMap.set(gradeType.id, gradeType)
          }
        })
        setGradeTypes(Array.from(typesMap.values()))

        const byType: Record<string, Grade[]> = {}
        grades.forEach((grade) => {
          const typeName = (grade.GraduationType || grade.type)?.name || 'Tipo Desconhecido'
          if (!byType[typeName]) {
            byType[typeName] = []
          }
          byType[typeName].push(grade)
        })

        Object.keys(byType).forEach((typeName) => {
          byType[typeName].sort((a, b) => {
            const aNum = parseInt(a.name)
            const bNum = parseInt(b.name)
            if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum
            return a.name.localeCompare(b.name)
          })
        })

        setGradesByType(byType)
      } catch (error) {
        console.error('Erro ao carregar grades:', error)
      }


      try {
        const res = await axios.get(`${FASHION_API_URL}/resource-categories`, { headers })
        const categories: ResourceCategory[] = Array.isArray(res.data) ? res.data : []
        setAllResourceCategories(categories)
      } catch (error) {
        console.error('Erro ao carregar resource categories:', error)
      }

      try {
        const res = await axios.get(`${FASHION_API_URL}/resources`, { headers })
        const resources: Resource[] = Array.isArray(res.data) ? res.data : []

        const byCategory: Record<number, Resource[]> = {}
        resources.forEach((resource) => {
          if (resource.categoryId) {
            if (!byCategory[resource.categoryId]) {
              byCategory[resource.categoryId] = []
            }
            byCategory[resource.categoryId].push(resource)
          }
        })
        setResourcesByCategory(byCategory)
      } catch (error) {
        console.error('Erro ao carregar resources:', error)
      }

      try {
        const res = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/services/service-stage`, { headers })
        const services: StageService[] = Array.isArray(res.data) ? res.data : []
        setAllServices(services)
      } catch (error) {
        console.error('Erro ao carregar services:', error)
      }

      try {
        const res = await axios.get(`${FASHION_API_URL}/colors`, { headers })
        const colors = Array.isArray(res.data) ? res.data : []
        setAllItemColors(colors.map((c: any) => ({ id: c.id, name: c.name })))
      } catch (error) {
        console.error('Erro ao carregar cores globais:', error)
      }

      try {
        const res = await axios.get(`${FASHION_API_URL}/sectors`, { headers })
        setSectors(Array.isArray(res.data) ? res.data : [])
      } catch (error) {
        console.error('Erro ao carregar setores de beneficiamento:', error)
      }

      setLoadingData(false)
    }

    fetchInitialData()
  }, [])


  useEffect(() => {
    if (sectors.length === 0) return
    setBeneficiamentoNotes(prev =>
      prev.map(n => (n.sectorId === 0 ? { ...n, sectorId: sectors[0].id } : n))
    )
  }, [sectors])

  useEffect(() => {
    if (!formData.collectionId) {
      setCollectionEpisodes([])
      setFilteredFamilies([])
      setFilteredGroups([])
      setAvailableGradeIds([])
      setAllCollectionColors([])
      setFamilyConjuntos([])
      setFamilyTrimInsumoOptions([])
      return
    }

    let cancelled = false

    const load = async () => {
      try {
        const token = localStorage.getItem('token')
        const res = await axios.get(
          `${FASHION_API_URL}/collections/${formData.collectionId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        const col = res.data
        if (cancelled) return

        const eps = col?.Episode ?? col?.Episodes
        const list: CollectionEpisodeOption[] = Array.isArray(eps)
          ? eps
              .filter((e: { id?: number }) => typeof e?.id === 'number')
              .map((e: { id: number; name?: string }) => ({
                id: e.id,
                name: String(e.name ?? ''),
              }))
          : []
        setCollectionEpisodes(list)
        setFormData((prev) => {
          if (!prev.episodeId) return prev
          if (!list.some((e) => e.id === prev.episodeId)) {
            return { ...prev, episodeId: 0 }
          }
          return prev
        })

        const rawFamilies = col?.Family ?? col?.Families
        const famList: Family[] = Array.isArray(rawFamilies)
          ? rawFamilies.map((f: any) => ({ id: f.id, name: f.name, collectionId: formData.collectionId }))
          : []
        setFilteredFamilies(famList)

        const rawGroups = col?.GroupInCollection ?? []
        const groupList: Group[] = Array.isArray(rawGroups)
          ? rawGroups
              .map((gic: any) => gic.Group || gic.group)
              .filter((g: any) => g && g.id)
          : []
        setFilteredGroups(groupList)



        const rawGrades = col?.GradeInCollection ?? []
        const gradeIds: number[] = Array.isArray(rawGrades)
          ? rawGrades.map((gic: any) => gic.gradeId ?? gic.Grade?.id).filter((id: any) => typeof id === 'number')
          : []
        setAvailableGradeIds(gradeIds)

        const rawColors = col?.CollectionColor ?? []
        const colorList = Array.isArray(rawColors)
          ? rawColors.map((c: any) => ({
              id: c.id,
              name: c.name,
              hex: c.hex || c.Pantone?.hex,
              pantoneCode: c.Pantone?.code || c.code,
            }))
          : []
        setAllCollectionColors(colorList)

        const rawMaterials = col?.CollectionMaterial ?? []
        if (Array.isArray(rawMaterials)) {
          const fabricMaterials = rawMaterials.filter((m: any) => m.kind !== 'TRIM')
          const trimMaterials = rawMaterials.filter((m: any) => m.kind === 'TRIM')

          const conjuntos: FamilyConjunto[] = fabricMaterials.map((cm: any) => {
            const stock = cm.Stock || null
            const stockMaterial = stock?.Material || null
            const stockMaterialName = stockMaterial?.name || cm.Material?.name || 'Material'
            const stockDescription = stock?.description ? String(stock.description) : ''
            const stockCode = stock?.codeId ? String(stock.codeId) : ''
            const stockLabelParts = [stockMaterialName, stockDescription, stockCode ? `Cód. ${stockCode}` : '']
              .map((p: string) => p.trim()).filter((p: string) => p.length > 0)
            const stockLabel = stock ? stockLabelParts.join(' - ') : null
            const resolvedMaterialId = cm.materialId || cm.Material?.id || stockMaterial?.id || null
            return {
              familyMaterialId: cm.id,
              collectionMaterialId: cm.id,
              materialId: resolvedMaterialId ?? 0,
              materialName: stockMaterialName,
              stockId: typeof cm.stockId === 'number' ? cm.stockId : null,
              stockLabel,
              finishing: getStockFinishing(stock),
              linkedResourceMaterials: [],
            }
          })
          setFamilyConjuntos(conjuntos)

          console.debug('[RC] trimMaterials loaded:', trimMaterials)
          const trimOpts: FamilyTrimInsumoOption[] = trimMaterials.map((cm: any) => {
            const tr = cm.TrimResource || {}
            console.debug('[RC] processing trimMaterial item:', cm.id, 'resource tr:', tr)
            const resourceId = typeof cm.resourceId === 'number' ? cm.resourceId : typeof tr.id === 'number' ? tr.id : null
            const materialId = typeof tr.materialId === 'number' ? tr.materialId : typeof cm.materialId === 'number' ? cm.materialId : 0
            const resourceName = tr.name ? String(tr.name) : null
            const categoryId = typeof tr.categoryId === 'number' ? tr.categoryId : tr.Category_Resource?.id ?? null
            const categoryName = tr.Category_Resource?.name ? String(tr.Category_Resource.name) : null
            const finishingVal = tr.Finishing?.type ? String(tr.Finishing.type) : (tr.finishing ? String(tr.finishing) : '—')
            console.debug('[RC] trimMaterial item:', cm.id, 'resolved finishing:', finishingVal)
            return {
              familyMaterialId: cm.id,
              collectionMaterialId: cm.id,
              resourceId,
              resourceName,
              materialId,
              materialName: resourceName || 'Insumo',
              categoryId,
              categoryName,
              finishing: finishingVal,
            }
          })
          console.debug('[RC] familyTrimInsumoOptions populated:', trimOpts)
          setFamilyTrimInsumoOptions(trimOpts)
        }
      } catch (err) {
        console.error('Erro ao carregar dados da coleção:', err)
        if (!cancelled) {
          setCollectionEpisodes([])
          setFilteredFamilies([])
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [formData.collectionId])

  useEffect(() => {
    if (!isEditMode || !productId) return

    const loadProduct = async () => {
      setIsInitializing(true)
      setProductExistingAttachments([])
      setProductAttachmentUrls([])

      try {
        const token = localStorage.getItem('token')
        const headers = { Authorization: `Bearer ${token}` }

        const res = await axios.get(`${FASHION_API_URL}/${productId}`, { headers })
        const product: ProductData = res.data

        const coverFileUrl = (product as any).CoverGeneralFile?.fileUrl
        const rawAttachments = ((product as any).Attachments || []).map((a: any) => a.GeneralFile?.fileUrl).filter(Boolean) as string[]
        const fashionAttachments = rawAttachments.map((url, index) => {
          const nameFromUrl = url.split('/').pop() || `anexo-${index + 1}`
          const decodedName = (() => {
            try {
              return decodeURIComponent(nameFromUrl)
            } catch {
              return nameFromUrl
            }
          })()
          const looksLikeImage = /\.(png|jpe?g|gif|bmp|webp|svg|avif|heic|heif|jfif)$/i.test(
            decodedName,
          )
          return {
            id: index + 1,
            originalName: decodedName,
            fileName: decodedName,
            filePath: url,
            fileUrl: url,
            fileSize: 0,
            mimeType: looksLikeImage ? 'image/jpeg' : '',
            isImage: looksLikeImage ? true : undefined,
            isCover: Boolean(coverFileUrl && url === coverFileUrl),
          }
        })

        applyProductAttachmentsState(fashionAttachments)

        rcDebug('=== PRODUTO CARREGADO ===')
        rcDebug('Product completo: ' + JSON.stringify(product, null, 2))
        rcDebug('Composition (back-end): ' + JSON.stringify(product.composition, null, 2))
        rcDebug('ProductItemColor entries (lowercase): ' + JSON.stringify(product.productItemColors || [], null, 2))
        rcDebug('ProductItemColor entries (uppercase): ' + JSON.stringify((product as any).ProductItemColor || [], null, 2))

        setProductData(product)

        const existingBeneficiamentoNotes: any[] = (product as any).BeneficiamentoNote || []
        if (existingBeneficiamentoNotes.length > 0) {
          const notesWithSignedUrls = await Promise.all(
            existingBeneficiamentoNotes.map(async (n: any) => {
              const noteUrls = (n.Files || []).map((f: any) => f.GeneralFile?.fileUrl).filter(Boolean) as string[]
              return {
                ...newBeneficiamentoNota(n.sectorId),
                id: n.id,
                sectorId: n.sectorId,
                description: n.description || '',
                imageOriginalUrls: noteUrls,
                imageUrls: await resolveNoteImageUrls(noteUrls),
              }
            })
          )
          setBeneficiamentoNotes(notesWithSignedUrls)
        }

        setFormData({
          codigo: product.productCode || '',
          nomeProduto: product.name || '',
          description: product.description || '',
          referenceProduct: product.referenceProduct || '',
          observations: product.observations || '',
          collectionId: product.collectionId || 0,
          familyId: product.familyId || 0,
          groupId: product.groupId || 0,
          episodeId: (product as any).episodeId ?? (product as any).Episode?.id ?? 0,
          price: (product as any).price || 0,
        });

        if (isEditMode) {
          initialEditFamilyIdRef.current = product.familyId || null
        }

        if (isEditMode && product.familyId) {
          const loadFamilyGrades = async () => {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            try {
              const res = await axios.get(
                `${FASHION_API_URL}/families/${product.familyId}/grades`,
                { headers }
              );
              const gradesInFamily = Array.isArray(res.data) ? res.data : [];
              const gradeIds = gradesInFamily.map((g: any) => g.gradeId);
              console.log('DEBUG GRADES - FORÇADO - Grades da família carregadas:', gradesInFamily);
              console.log('DEBUG GRADES - FORÇADO - gradeIds:', gradeIds);
              setAvailableGradeIds(gradeIds);


              setTimeout(() => {
                const gradeInProduct = productData?.gradeInProduct || (productData as any)?.GradeInProduct || [];

                if (gradeInProduct?.length) {
                  const gradeIdsFromProduct = gradeInProduct.map((g: any) => {
                    const gradeId = g.gradeId || (g.Grade ? g.Grade.id : g.gradeId);
                    return gradeId;
                  }).filter(Boolean);

                  const validGradeIds = gradeIdsFromProduct.filter((gradeId: number) => {
                    const isIncluded = gradeIds.some((g: any) => {
                      const id = typeof g === 'object' ? g.id : g;
                      return id === gradeId;
                    });
                    return isIncluded;
                  });

                  if (validGradeIds.length > 0) {
                    const validGradeObjects = validGradeIds.map((gradeId: number) => {
                      const gradeObj = gradeIds.find((g: any) => {
                        const id = typeof g === 'object' ? g.id : g;
                        return id === gradeId;
                      });
                      return gradeObj || gradeId;
                    });

                    setSelectedGradeIds(validGradeObjects);

                    if (validGradeObjects.length > 0) {
                      const firstGrade = validGradeObjects[0];
                      if (typeof firstGrade === 'object' && firstGrade !== null) {
                        const gradeAsAny = firstGrade as any;
                        const gradeType = gradeAsAny.GraduationType?.id || gradeAsAny.typeId;
                        if (gradeType) {
                          setSelectedGradeType(gradeType);
                        }
                      }
                    }
                  }
                }
              }, 100);
            } catch (error) {
              console.log('DEBUG GRADES - FORÇADO - Erro ao carregar grades da família:', error);
            }
          };

          loadFamilyGrades();
        }

        setTimeout(() => {
          setFormData(prev => ({
            ...prev,
            groupId: product.groupId || 0
          }));

          if (product.groupId) {
            setFilteredGroups(prev => {
              if (!prev.some(g => g.id === product.groupId)) {
                return [...prev, {
                  id: product.groupId,
                  name: product.Group?.name || `Grupo ${product.groupId}`
                }];
              }
              return prev;
            });
          }

          console.log('DEBUG FORM DATA ATUALIZADO:', {
            groupId: product.groupId,
            groupName: product.Group?.name,
            filteredGroups: filteredGroups,
            filteredGroupsLength: filteredGroups.length
          });
        }, 100);

        console.log('DEBUG GRADES - gradeIds do produto:', product.gradeInProduct?.map(g => g.gradeId) || []);
        console.log('DEBUG GRADES - availableGradeIds:', availableGradeIds);
        console.log('DEBUG GRADES - product.gradeInProduct:', product.gradeInProduct);

        if (product.gradeInProduct?.length || (product as any).GradeInProduct?.length) {
          const gradeInProduct = product.gradeInProduct || (product as any).GradeInProduct || [];
          if (gradeInProduct.length > 0) {
            const firstGradeData = gradeInProduct[0];

            const firstGrade = firstGradeData.grade || firstGradeData.Grade;
            const typeId = firstGrade?.GraduationType?.id || firstGrade?.type?.id || firstGradeData.grade?.GraduationType?.id;
            if (typeId) setSelectedGradeType(typeId);
          }
        }

        const buildScriptStockLabel = (stk: any, fallbackMaterialName?: string | null) => {
          if (!stk) return ''
          const matName = stk?.Material?.name || fallbackMaterialName || ''
          const desc = stk?.description ? String(stk.description) : ''
          const code = stk?.codeId ? `Cód. ${stk.codeId}` : ''
          const parts = [matName, desc, code]
            .map((p) => String(p ?? '').trim())
            .filter((p) => p.length > 0)
          return parts.join(' - ') || `Stock #${stk?.id ?? ''}`
        }

        let itemIdCounter = 1;
        if (product.Script?.length) {
          const items = product.Script.flatMap((script: any) => {
            const scriptItems: any[] = [];

            if (script.ScriptMaterial?.length) {
              script.ScriptMaterial.forEach((sm: any) => {
                if (sm.Material?.id) {
                  const material = sm.Material;
                  const persistedStock = sm.Stock || null;
                  const fallbackStock = material.Stock?.[0] || null;
                  const stockForLocation = persistedStock || fallbackStock;
                  const stockIdValue = sm.stockId ?? persistedStock?.id ?? null;
                  const descricao = persistedStock
                    ? buildScriptStockLabel(persistedStock, material.name)
                    : '';

                  scriptItems.push({
                    id: itemIdCounter++,
                    tipo: 'materia-prima' as const,
                    materialId: material.id,
                    stockId: stockIdValue,
                    resourceId: null,
                    categoryId: null,
                    descricao,
                    unidadeMedida: sm.UnitOfMeasurement?.name || '',
                    unitOfMeasurementId: sm.unitOfMeasurementId || null,
                    localEstoque: stockForLocation?.StockLocation?.name || '',
                    consumo: sm.weight?.toString() || sm.idealMeasurements?.toString() || '',
                    costPerUnit: scriptCostPerUnitToInput(sm.costPerUnit),
                    finishing: getStockFinishing(persistedStock),
                    availableVariations: [],
                    colorMappings: [],
                  });
                }
              });
            }


            if (script.ScriptResource?.length) {
              script.ScriptResource.forEach((sr: any) => {
                if (sr.Resource?.id) {
                  const resource = sr.Resource;


                  let derivedMaterialId: number | null = resource.materialId ?? null;
                  let derivedCategoryId: number | null = resource.categoryId ?? null;
                  if (!derivedMaterialId && resourcesByCategory) {
                    for (const [catId, resources] of Object.entries(resourcesByCategory)) {
                      const found = (resources || []).find((r) => r.id === resource.id);
                      if (found) {
                        derivedMaterialId = found.materialId ?? derivedMaterialId;
                        derivedCategoryId = Number(catId);
                        break;
                      }
                    }
                  }

                  const persistedStock = sr.Stock || null;
                  const stockIdValue = sr.stockId ?? persistedStock?.id ?? null;

                  scriptItems.push({
                    id: itemIdCounter++,
                    tipo: 'insumo' as const,
                    materialId: derivedMaterialId,
                    stockId: stockIdValue,
                    resourceId: resource.id,
                    categoryId: derivedCategoryId,
                    descricao: resource.description || resource.name || '',
                    unidadeMedida: sr.UnitOfMeasurement?.name || '',
                    unitOfMeasurementId: sr.unitOfMeasurementId || null,
                    localEstoque: '',
                    consumo: sr.weight?.toString() || '',
                    costPerUnit: scriptCostPerUnitToInput(sr.costPerUnit),
                    finishing: resource.Finishing?.type ? String(resource.Finishing.type) : (resource.finishing ? String(resource.finishing) : '—'),
                    availableVariations: [],
                    colorMappings: [],
                  });
                }
              });
            }

            if (script.ScriptStage?.length) {
              script.ScriptStage.forEach((st: any) => {
                if (st.Stage?.id) {
                  const stage = st.Stage;
                  scriptItems.push({
                    id: itemIdCounter++,
                    tipo: 'servico' as const,
                    materialId: null,
                    stockId: null,
                    resourceId: null,
                    stageId: stage.id,
                    categoryId: null,
                    descricao: stage.name || stage.description || '',
                    unidadeMedida: st.UnitOfMeasurement?.name || '',
                    unitOfMeasurementId: st.unitOfMeasurementId || null,
                    localEstoque: '',
                    consumo: st.deliveryTime || '',
                    costPerUnit: scriptCostPerUnitToInput(st.costPerUnit),
                    availableVariations: [],
                    colorMappings: [],
                  });
                }
              });
            }

            return scriptItems;
          });

          console.log('Items de composição criados a partir do Script:', items);

          const serverColors: any[] =
            (product as any).productItemColors ||
            (product as any).ProductItemColor ||
            [];
          rcDebug('[RC] serverColors do produto: ' + JSON.stringify(serverColors, null, 2));
          console.debug('[RC] serverColors with StockColors info', serverColors.map(sc => ({
            id: sc.id,
            stockColorId: sc.stockColorId,
            stockInfo: sc.StockColors ? { stockId: sc.StockColors.stockId, materialId: sc.StockColors.Stock?.materialId } : null
          })));

          setAllItemColors(prev => {
            const extras: { id: number; name: string }[] = [];
            serverColors.forEach((sc: any) => {
              let name: string | null = null;
              let id: number | null = null;
              if (sc.Stock?.ItemColor?.name) {
                name = sc.Stock.ItemColor.name;
                id = sc.Stock.ItemColor.id ?? sc.Stock.itemColorId;
              } else if (sc.Resource?.ItemColor?.name) {
                name = sc.Resource.ItemColor.name;
                id = sc.Resource.ItemColor.id ?? sc.Resource.itemColorId;
              } else if (sc.ItemColor?.name) {
                name = sc.ItemColor.name;
                id = sc.itemColorId;
              }
              if (name && id != null && !prev.some(c => c.id === id) && !extras.some(c => c.id === id)) {
                extras.push({ id, name });
              }
            });
            return extras.length ? [...prev, ...extras] : prev;
          });
          console.debug('[RC] composition items before mapping colors', items);
          let itemsWithColors = items.map((item) => {
            const related = serverColors.filter((pc) => {
              if (item.stockId) {
                if (pc.stockId === item.stockId || pc.Stock?.id === item.stockId) {
                  return true;
                }
              }

              if (item.materialId) {
                if (pc.Stock?.materialId === item.materialId) {
                  return true;
                }
              }

              if (item.resourceId) {
                if (pc.resourceId === item.resourceId) return true;
                if (pc.Resource?.id === item.resourceId) return true;
              }

              if (item.tipo !== 'materia-prima' && item.materialId && pc.Resource?.materialId === item.materialId) {
                return true;
              }
            });

            const mapped = related.map((pc: any) => {
              const resolvedItemColorId =
                pc.Stock?.ItemColor?.id ??
                pc.Stock?.itemColorId ??
                pc.Resource?.ItemColor?.id ??
                pc.Resource?.itemColorId ??
                pc.itemColorId ??
                null;
              return {
                id: pc.id,
                collectionColorId: pc.collectionColorId,
                itemColorId: resolvedItemColorId,
                itemColorName: pc.Stock?.ItemColor?.name ?? pc.Resource?.ItemColor?.name ?? null,
                materialId: pc.materialId,
                resourceId: pc.resourceId || (pc.Resource ? pc.Resource.id : null),
                stockId: pc.stockId || pc.Stock?.id || null,
              }
            });

            const newItem = { ...item, productItemColors: mapped.length ? mapped : item.productItemColors } as CompositionItem & { stockId?: number };
            if (newItem.tipo === 'materia-prima') {
              const relWithStock = related.find((pc: any) => pc.stockId || pc.Stock?.id);
              if (relWithStock) {
                const sid = relWithStock.stockId || relWithStock.Stock?.id;
                if (sid && sid !== newItem.stockId) {
                  newItem.stockId = sid;
                  console.debug('[RC] assigned stockId from serverColors', newItem.stockId, 'for item', item.id);
                }
              }
            }
            return newItem;
          });

          itemsWithColors = await Promise.all(itemsWithColors.map(async (it) => {
            if (it.tipo === 'materia-prima') {
              if (!it.stockId && it.materialId) {
                try {
                  const token = localStorage.getItem('token')
                  const headers = token ? { Authorization: `Bearer ${token}` } : {}
                  const res = await axios.get(`${FASHION_API_URL}/stocks`, { headers, params: { materialId: it.materialId } })
                  const stocks: any[] = Array.isArray(res.data) ? res.data : []
                  if (stocks.length > 0) {
                    it.stockId = stocks[0].id
                  }
                } catch (e) {
                  console.error('erro ao buscar stock para material (edição)', e)
                }
              }

              if (it.stockId && !it.materialId) {
                try {
                  const token = localStorage.getItem('token')
                  const headers = token ? { Authorization: `Bearer ${token}` } : {}
                  const res = await axios.get(`${FASHION_API_URL}/stocks/${it.stockId}`, { headers })
                  if (res.data && res.data.materialId) {
                    it.materialId = res.data.materialId
                  }
                } catch (e) {
                  console.error('erro ao buscar material a partir do stock (edição)', e)
                }
              }
            }
            return it
          }));

          rcDebug('Items de composição após anexar cores do servidor: ' + JSON.stringify(itemsWithColors, null, 2));
          setCompositionItems(itemsWithColors);
          rcDebug('[RC] composição inicializada (itemsWithColors): ' + JSON.stringify(itemsWithColors, null, 2));

          (async () => {
            const enriched = await Promise.all(itemsWithColors.map(async (it) => {
              let variations: any[] = [];
              if (it.tipo === 'materia-prima') {
                const stockIds = new Set<number>();

                if (it.stockId) stockIds.add(it.stockId);
                (it.productItemColors || []).forEach((pc: any) => {
                  if (pc.stockId) stockIds.add(pc.stockId);
                  else if (pc.StockColors?.stockId) stockIds.add(pc.StockColors.stockId);
                });

                if (stockIds.size) {
                  for (const sid of stockIds) {
                    const vs = await fetchStockColors(sid);
                    variations.push(...vs);
                  }
                } else if (it.materialId) {
                  variations = await fetchStockColors(undefined, it.materialId);
                }

                variations = variations.filter((v, i, a) => a.findIndex(x => x.id === v.id) === i);
              } else if (it.resourceId) {
                variations = await fetchResourceColors(it.resourceId);
              }

              const mapped = variations.map(v => ({
                id: v.id,
                colorId: v.colorId,
                stockId: it.stockId || null,
                resourceId: it.resourceId || null,
                color: { id: v.id, name: v.color.name },
                stock: null,
                resource: null,
              }));

              setAllVariations(prev => {
                const combined = [...prev];
                mapped.forEach(m => { if (!combined.some(x => x.id === m.id)) combined.push(m); });
                return combined;
              });

              const colorMappings = mapped.map(m => {
                let selectedVariation: number | null = null;
                if (it.productItemColors && Array.isArray(it.productItemColors)) {
                  const matching = it.productItemColors.find((pc: any) =>
                    (pc.itemColorId && (pc.itemColorId === m.id || pc.itemColorId === m.colorId)) ||
                    (pc.itemColorName && pc.itemColorName === m.color?.name)
                  );
                  if (matching) selectedVariation = m.id;
                }
                return {
                  mainVariationId: m.id,
                  mainColorName: m.color.name,
                  itemVariationId: selectedVariation,
                };
              });

              console.debug('[RC] item', it.id, 'variations', mapped, 'mappings', colorMappings);

              return {
                ...it,
                availableVariations: mapped,
                colorMappings,
              };
            }));
            setCompositionItems(enriched);
            rcDebug('[RC] composição enriquecida com variações por item: ' + JSON.stringify(enriched, null, 2));
          })();

          if (product.Script) {
            const allColorIds: number[] = [];
            const colorMap: Record<number, string> = {};

            product.Script.forEach(script => {
              if (script.ScriptMaterial) {
                script.ScriptMaterial.forEach(sm => {
                  if (sm.Material?.Stock) {
                    sm.Material.Stock.forEach(stock => {
                      if (stock.StockColors) {
                        stock.StockColors.forEach(sc => {
                          if (!allColorIds.includes(sc.id)) {
                            allColorIds.push(sc.id);
                          }

                          if (sc.ItemColor?.name) {
                            colorMap[sc.id] = sc.ItemColor.name;
                          }
                        });
                      }
                    });
                  }
                });
              }
            });

            const pics = ((product as any).ProductItemColor || []) as any[];
            pics.forEach(pic => {
              if (pic.stockColorId && typeof pic.stockColorId === 'number') {

                if (!allColorIds.includes(pic.stockColorId)) {
                  allColorIds.push(pic.stockColorId);
                }
              }
            });

            rcDebug('DEBUG CORES - Cores encontradas no Script: ' + JSON.stringify(allColorIds));
            rcDebug('DEBUG CORES - Mapa de cores: ' + JSON.stringify(colorMap));
            rcDebug('[RC] baseFabricColors candidatos: ' + JSON.stringify(allColorIds.map(id => {
              const varRec = allVariations.find(v => v.id === id);
              return {
                variationId: id,
                colorId: varRec ? varRec.colorId : id,
                colorName: colorMap[id] || `Cor ${id}`,
              };
            }), null, 2));

            if (allColorIds.length > 0) {
              setSelectedStockColorIds(allColorIds);
            }
          }
        } else {
          rcDebug('[RC] produto sem Script (composição) no retorno da API.');

          const variations = (product as any).composition?.variations || [];
          rcDebug('[RC] product.composition.variations (fallback): ' + JSON.stringify(variations, null, 2));

          if (variations.length) {
            const items = variations.map((v: any) => {
              const isMateriaPrima = Boolean(v.stockId);
              return {
                id: itemIdCounter++,
                tipo: isMateriaPrima ? 'materia-prima' as const : 'insumo' as const,
                materialId: v.stock?.materialId ?? null,
                stockId: v.stockId ?? null,
                resourceId: v.resourceId ?? null,
                categoryId: null,
                descricao: v.description || '',
                unidadeMedida: '',
                unitOfMeasurementId: null,
                localEstoque: v.stockLocation || '',
                consumo: v.usage != null ? String(v.usage) : '',
                finishing: isMateriaPrima ? getStockFinishing(v.stock) : (v.resource?.Finishing?.type ? String(v.resource.Finishing.type) : (v.resource?.finishing ? String(v.resource.finishing) : '—')),
                availableVariations: [],
                colorMappings: [],
              };
            });
            console.debug('[RC] Items de composição criados a partir de product.composition:', items);
            setCompositionItems(items);
          } else {
            rcDebug('[RC] product.composition também vazio - nenhuma composição carregada.');
          }
        }

        setTimeout(() => {
          setFormData(prev => ({
            ...prev,
            groupId: product.groupId || 0
          }));
        }, 100);
        setIsInitializing(false)
        initializationCompletedRef.current = true
      } catch (error) {
        console.error('Erro ao carregar produto:', error)
        setFeedback({
          isOpen: true,
          statusOk: false,
          message: 'Erro ao carregar dados do produto.',
        })
        setIsInitializing(false)
      }
    }

    loadProduct();

    return () => {
      isInitialLoadRef.current = true
      initializationCompletedRef.current = false
    }
  }, [isEditMode, productId]);

  useEffect(() => {
    if (!isEditMode || !productData) return
    if (!allCollectionColors || allCollectionColors.length === 0) return

    try {
      const productColors = (productData as any).ProductItemColor || []
      const collectionColorIds: number[] = productColors
        .map((pc: any) => pc.collectionColorId)
        .filter((id: any) => id !== null && id !== undefined)
      const uniqueIds = Array.from(new Set(collectionColorIds))

      const selected = uniqueIds.map(id => {
        const found = allCollectionColors.find(c => Number(c.id) === Number(id));
        if (found) return found;

        const pic = (productData as any)?.ProductItemColor?.find((p: any) => p.collectionColorId === id);
        const name = pic?.ItemColor?.name || pic?.ResourceColor?.ItemColor?.name || pic?.stockColorId &&
          (pic.ItemColor?.name || `Cor ${pic.stockColorId}`) || `Cor ${id}`;
        return { id, name };
      });

      if (selected.length > 0) {
        setSelectedCollectionColors(selected);
      }
    } catch (err) {
      console.error('Erro ao popular selectedCollectionColors no modo edição:', err)
    }
  }, [isEditMode, productData, allCollectionColors])



  useEffect(() => {
    if (!isEditMode) return;

    if (selectedGradeIds.length > 0 && availableGradeIds.length > 0) {
      selectedGradeIds.forEach(gradeId => {
        const gradeDetail = Array.isArray(availableGradeIds) && typeof availableGradeIds[0] === 'object'
          ? availableGradeIds.find((g: any) => g.id === gradeId)
          : null;

        if (gradeDetail && typeof gradeDetail !== 'number') {
          console.log(`DEBUG GRADES - Informações completas da grade ${gradeId}:`, {
            id: (gradeDetail as any).id,
            name: (gradeDetail as any).name,
            typeId: (gradeDetail as any).typeId,
            typeName: (gradeDetail as any).GraduationType?.name || (gradeDetail as any).type?.name || 'Tipo não encontrado'
          });
        } else {

          if (Array.isArray(availableGradeIds) && typeof availableGradeIds[0] === 'number') {
            console.log(`DEBUG GRADES - Grade ${gradeId} está na lista de IDs disponíveis`);
          } else {
            console.log(`DEBUG GRADES - Grade ${gradeId} não encontrada em availableGradeIds`);
          }
        }

        try {
          const productItemColors = (productData as any)?.ProductItemColor || []
          console.debug('[RC] productItemColors do servidor', productItemColors)
          if (productItemColors.length && compositionItems.length) {
            const updated = compositionItems.map((it: any) => ({ ...it, productItemColors: it.productItemColors ? [...it.productItemColors] : [] }))
            productItemColors.forEach((pic: any) => {
              let derivedItemColorId: number | null = null
              if (pic.itemColorId) derivedItemColorId = pic.itemColorId
              else if (pic.ItemColor?.id) derivedItemColorId = pic.ItemColor.id
              else if (pic.Stock?.itemColorId) derivedItemColorId = pic.Stock.itemColorId
              else if (pic.StockColors?.ItemColor?.id) derivedItemColorId = pic.StockColors.ItemColor.id
              else if (pic.StockColors?.colorId) derivedItemColorId = pic.StockColors.colorId
              else if (pic.Resource?.itemColorId) derivedItemColorId = pic.Resource.itemColorId

              const match = updated.find((u: any) => {
                if (pic.resourceId && u.resourceId === pic.resourceId) return true
                if (pic.Resource?.id && u.resourceId === pic.Resource.id) return true
                if (u.materialId && pic.Resource?.materialId && u.materialId === pic.Resource.materialId) return true
                if (u.materialId && pic.materialId && u.materialId === pic.materialId) return true
                if (u.stockId && pic.stockId && u.stockId === pic.stockId) return true
                if (u.stockId && (pic.Stock?.id || pic.StockColors?.stockId) && u.stockId === (pic.Stock?.id || pic.StockColors?.stockId)) return true
                return false
              })

              if (match) {
                const prevArr = match.productItemColors || []
                prevArr.push({
                  id: pic.id,
                  collectionColorId: pic.collectionColorId,
                  itemColorId: derivedItemColorId,
                  stockColorId: pic.stockColorId || null,
                  resourceId: pic.resourceId || (pic.Resource ? pic.Resource.id : null),
                })
                match.productItemColors = prevArr
              }
            })

            setCompositionItems(updated)
            console.debug('[RC] compositionItems após mapear colors', updated)
          }
        } catch (mapErr) {
          console.error('Erro ao mapear ProductItemColor para compositionItems:', mapErr)
        }
      });
    }
  }, [isEditMode, selectedGradeIds, availableGradeIds]);




  useEffect(() => {
    if (!isEditMode || !productData) {
      return;
    }

    if (!availableGradeIds || availableGradeIds.length === 0) {
      const timer = setTimeout(() => {
        if (productData && availableGradeIds && availableGradeIds.length > 0) {

          const gradeInProduct = productData.gradeInProduct || (productData as any).GradeInProduct || [];

          if (gradeInProduct?.length) {
            const gradeIdsFromProduct = gradeInProduct.map((g: any) => {
              const gradeId = g.gradeId || (g.Grade ? g.Grade.id : g.gradeId);
              return gradeId;
            }).filter(Boolean);

            const validGradeIds = gradeIdsFromProduct.filter((gradeId: number) => {
              const isIncluded = availableGradeIds.some((g: any) => {
                const id = typeof g === 'object' ? g.id : g;
                return id === gradeId;
              });
              return isIncluded;
            });

            if (validGradeIds.length > 0) {
              const validGradeObjects = validGradeIds.map((gradeId: number) => {
                const gradeObj = availableGradeIds.find((g: any) => {
                  const id = typeof g === 'object' ? g.id : g;
                  return id === gradeId;
                });
                return gradeObj || gradeId;
              });

              setSelectedGradeIds(validGradeObjects);

              if (validGradeObjects.length > 0) {
                const firstGrade = validGradeObjects[0];
                if (typeof firstGrade === 'object' && firstGrade !== null) {
                  const gradeAsAny = firstGrade as any;
                  const gradeType = gradeAsAny.GraduationType?.id || gradeAsAny.typeId;
                  if (gradeType) {
                    setSelectedGradeType(gradeType);
                  }
                }
              }
            }
          }
        }
      }, 100);

      return () => clearTimeout(timer);
    }

    const gradeInProduct = productData.gradeInProduct || (productData as any).GradeInProduct || [];

    if (gradeInProduct?.length) {
      const gradeIdsFromProduct = gradeInProduct.map((g: any) => {
        const gradeId = g.gradeId || (g.Grade ? g.Grade.id : g.gradeId);
        return gradeId;
      }).filter(Boolean);

      const validGradeIds = gradeIdsFromProduct.filter((gradeId: number) => {
        const isIncluded = availableGradeIds.some((g: any) => {
          const id = typeof g === 'object' ? g.id : g;
          return id === gradeId;
        });
        return isIncluded;
      });

      if (validGradeIds.length > 0) {
        const validGradeObjects = validGradeIds.map((gradeId: number) => {
          const gradeObj = availableGradeIds.find((g: any) => {
            const id = typeof g === 'object' ? g.id : g;
            return id === gradeId;
          });
          return gradeObj || gradeId;
        });

        setSelectedGradeIds(validGradeObjects);

        if (validGradeObjects.length > 0) {
          const firstGrade = validGradeObjects[0];
          if (typeof firstGrade === 'object' && firstGrade !== null) {
            const gradeAsAny = firstGrade as any;
            const gradeType = gradeAsAny.GraduationType?.id || gradeAsAny.typeId;
            if (gradeType) {
              setSelectedGradeType(gradeType);
            }
          }
        }
      }
    }
  }, [isEditMode, productData, availableGradeIds]);


  useEffect(() => {
    const previousLength = selectedCollectionColorsPrevLength.current
    const currentLength = selectedCollectionColors.length

    if (currentLength > 0 && previousLength === 0) {
      const el = document.getElementById('composition-section');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }

    selectedCollectionColorsPrevLength.current = currentLength

    if (!currentLength) {
      setCompositionItems(prev =>
        prev.map(item => ({ ...item, productItemColors: [] })),
      )
      return
    }

    setCompositionItems(prev => prev.map(item => {
      const prevArr: ProductItemColorEntry[] = item.productItemColors ? [...item.productItemColors] : []

      selectedCollectionColors.forEach(col => {
        if (!prevArr.some(p => p.collectionColorId === col.id)) {
          prevArr.push({ collectionColorId: col.id, itemColorId: null })
        }
      })

      const filtered = prevArr.filter(p => selectedCollectionColors.some(sc => sc.id === p.collectionColorId))
      return { ...item, productItemColors: filtered }
    }))
  }, [selectedCollectionColors])




  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    if (name === 'familyId') {
      setFormData((prev) => ({
        ...prev,
        familyId: Number(value),
      }))
      return
    }

    setFormData((prev) => ({
      ...prev,
      [name]: name.includes('Id') ? Number(value) : value,
    }))
  }

  const handleGradeTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const typeId = Number(e.target.value)
    setSelectedGradeType(typeId || null)
    setSelectedGradeIds([])
  }

  const handleGradeToggle = (gradeId: number) => {
    if (!availableGradeIds.includes(gradeId)) return

    setSelectedGradeIds((prev) =>
      prev.includes(gradeId) ? prev.filter((id) => id !== gradeId) : [...prev, gradeId]
    )
  }



  const handleStockColorSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const stockId = Number(e.target.value)
    if (!stockId) return
    const stock = conjuntoStocks.find(s => s.id === stockId)
    if (!stock) return
    const scIds: number[] = (stock.StockColors || []).map((sc: any) => sc.id)
    setSelectedStockColorIds(prev => Array.from(new Set([...prev, ...scIds])))
  }

  const removeStockColor = (stockId: number) => {

    setSelectedStockColorIds(selectedStockColorIds.filter(id => id !== stockId))
  }

  const addCompositionItem = () => {
    const newId = compositionItems.length > 0 ? Math.max(...compositionItems.map((i) => i.id)) + 1 : 1
    const hasMaterialItem = compositionItems.some((i) => i.tipo === 'materia-prima')
    const isFirst = compositionItems.length === 0

    setCompositionItems([
      ...compositionItems,
      {
        id: newId,
        tipo: isFirst && !hasMaterialItem ? 'materia-prima' : 'insumo',
        stockId: null,
        resourceId: null,
        categoryId: null,
        descricao: '',
        unidadeMedida: '',
        unitOfMeasurementId: null,
        localEstoque: '',
        consumo: '',
        costPerUnit: '',
        finishing: null,
        availableVariations: [],
        productItemColors: selectedCollectionColors.map(c => ({ collectionColorId: c.id, itemColorId: null })),
      },
    ])
  }

  const removeCompositionItem = (id: number) => {
    setCompositionItems(compositionItems.filter((item) => item.id !== id))
  }

  const handleCompositionTypeChange = (itemId: number, tipo: 'materia-prima' | 'insumo' | 'servico' | '') => {
    setCompositionItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
            ...item,
            tipo,
            materialId: null,
            stockId: null,
            resourceId: null,
            stageId: null,
            categoryId: null,
            descricao: '',
            unidadeMedida: '',
            consumo: '',
            costPerUnit: '',
            finishing: null,
            availableVariations: [],
            productItemColors: item.productItemColors,
          }
          : item
      )
    )
  }

  const handleMaterialSelect = async (itemId: number, familyMaterialId: number) => {
    const conjunto = familyConjuntos.find(c => c.familyMaterialId === familyMaterialId)
    const matId = conjunto?.materialId || null
    let variations: MaterialVariation[] = []
    if (matId) {
      try {
        const token = localStorage.getItem('token')
        const headers = { Authorization: `Bearer ${token}` }
        const res = await axios.get(`${FASHION_API_URL}/stocks`, {
          headers,
          params: { materialId: matId },
        })
        const stocks = Array.isArray(res.data) ? res.data : []
        const colorsArr: { id: number, name: string, stockId: number }[] = []
        for (const stk of stocks) {
          const cols = await fetchStockColors(stk.id)
          cols.forEach((c: any) => {
            if (!colorsArr.some(x => x.id === c.id)) colorsArr.push({ id: c.id, name: c.color.name, stockId: stk.id })
          })
        }
        variations = colorsArr.map((c: any) => ({ id: c.id, colorId: c.colorId || c.id, stockId: c.stockId, resourceId: null, color: { id: c.colorId || c.id, name: c.name }, stock: null, resource: null }))
      } catch (e) {
        console.error('erro ao buscar variações para material', e)
      }
    }

    setCompositionItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
            ...item,
            materialId: matId,
            stockId: conjunto?.stockId ?? null,
            resourceId: null,
            descricao: conjunto?.stockLabel || conjunto?.materialName || item.descricao,
            finishing: conjunto?.finishing || null,
            availableVariations: variations,
            productItemColors: item.productItemColors,
          }
          : item
      )
    )
  }

  const handleServiceSelect = (itemId: number, stageId: number) => {
    const stage = allServices.find(s => s.id === stageId)
    setCompositionItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
            ...item,
            tipo: 'servico',
            stageId,
            materialId: null,
            stockId: null,
            resourceId: null,
            categoryId: null,
            descricao: stage?.name || item.descricao,
            availableVariations: [],
            productItemColors: item.productItemColors,
          }
          : item
      )
    )
  }

  const handleProductAttachmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    if (!files.length) return

    const valid: File[] = []
    const invalid: File[] = []
    for (const file of files) {
      if (isProductImageFile(file) || isAllowedDocumentUpload(file)) {
        valid.push(file)
      } else {
        invalid.push(file)
      }
    }

    if (valid.length > 0) {
      setProductSelectedFiles((prev) => [...prev, ...valid])
    }

    if (invalid.length > 0) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message:
          invalid.length === 1
            ? `Tipo de arquivo não permitido: ${invalid[0].name}. Use imagens ou documentos (PDF, Office, TXT, CSV, RTF).`
            : `${invalid.length} arquivo(s) ignorados por tipo não permitido. Use imagens ou documentos (PDF, Office, TXT, CSV, RTF).`,
      })
    }

    if (event.target) event.target.value = ''
  }

  const handleProductAttachmentRemove = (index: number) => {
    setProductSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleViewProductExistingAttachment = async (attachment: ExistingAttachment) => {
    const url = await resolveProductAttachmentAccessUrl(attachment, 'view')
    if (!url) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Não foi possível abrir o anexo.',
      })
      return
    }

    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleDownloadProductExistingAttachment = async (attachment: ExistingAttachment) => {
    const url = await resolveProductAttachmentAccessUrl(attachment, 'download')
    if (!url) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Não foi possível baixar o anexo.',
      })
      return
    }

    await downloadRemoteFile(url, attachment.originalName || attachment.fileName || 'anexo')
  }

  const handleProductExistingAttachmentRemove = async (attachmentId: number) => {
    const targetAttachment = productExistingAttachments.find((attachment) => attachment.id === attachmentId)
    if (!targetAttachment) return

    setProductExistingAttachments((prev) => prev.filter((attachment) => attachment.id !== attachmentId))
    setProductAttachmentUrls((prev) =>
      prev.filter(
        (url) =>
          url !== (targetAttachment.fileUrl || '') &&
          url !== (targetAttachment.filePath || ''),
      ),
    )
  }

  const handleSetProductAttachmentCover = async (attachment: ExistingAttachment) => {
    const attachmentSource = attachment.fileUrl || attachment.filePath || ''

    setProductExistingAttachments((prev) =>
      prev.map((item) => {
        const itemSource = item.fileUrl || item.filePath || ''
        const isSameItemById = item.id === attachment.id
        const isSameItemBySource = Boolean(attachmentSource) && itemSource === attachmentSource

        return {
          ...item,
          isCover: isSameItemById || isSameItemBySource,
        }
      }),
    )
  }

  const handleRemoveProductAttachmentCover = async () => {
    setProductExistingAttachments((prev) =>
      prev.map((item) => ({ ...item, isCover: false })),
    )
  }

  const handleInsumoGroupSelect = async (itemId: number, materialAndCategory: string | null) => {
    if (!materialAndCategory) return
    const raw = String(materialAndCategory)

    let selectedGroupResourceId: number | null = null
    let selectedMaterialId: number | null = null
    let selectedCategoryId: number | null = null

    if (raw.startsWith('r:')) {
      selectedGroupResourceId = Number(raw.slice(2)) || null
    } else {
      const [materialRaw, categoryRaw] = raw.split(':')
      selectedMaterialId = materialRaw ? Number(materialRaw) : null
      selectedCategoryId = categoryRaw ? Number(categoryRaw) : null
    }

    const selectedGroup = insumoMaterialGroups.find(g => {
      if (selectedGroupResourceId !== null) {
        return g.resourceId === selectedGroupResourceId
      }
      return (
        g.materialId === selectedMaterialId &&
        Number(g.categoryId ?? 0) === Number(selectedCategoryId ?? 0)
      )
    }) || null

    if (selectedGroup) {
      selectedMaterialId = selectedGroup.materialId ?? selectedMaterialId
      selectedCategoryId = selectedGroup.categoryId ?? selectedCategoryId
    }

    const resolvedResourceId =
      selectedGroupResourceId ??
      selectedGroup?.resourceId ??
      (selectedMaterialId
        ? getTemplateResourceIdByMaterial(selectedMaterialId, selectedCategoryId)
        : null)

    if (!resolvedResourceId) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Não foi possível localizar o recurso correspondente para o insumo selecionado.',
      })
      return;
    }

    const colors = selectedMaterialId
      ? await fetchResourceMaterialColors(selectedMaterialId)
      : []
    const variations: MaterialVariation[] = colors.map((c: any) => ({
      id: c.id,
      colorId: c.colorId || c.id,
      stockId: null,
      resourceId: resolvedResourceId,
      color: { id: c.colorId || c.id, name: c.color?.name || c.name || '' },
      stock: null,
      resource: null,
    }))

    setCompositionItems(prev => {
      const next = prev.map(item =>
        item.id === itemId
          ? {
            ...item,
            materialId: selectedMaterialId,
            stockId: null,
            resourceId: resolvedResourceId,
            categoryId: selectedCategoryId,
            descricao: selectedGroup?.materialName || item.descricao,
            availableVariations: variations,
            finishing: selectedGroup?.finishing || '—',

            productItemColors: selectedCollectionColors.map(c => ({ collectionColorId: c.id, itemColorId: null })),
          }
          : item
      );
      const updated = next.find(i => i.id === itemId);
      console.debug('[RC] handleInsumoGroupSelect updated item', updated);
      return next;
    });
  }

  const fetchStockColors = async (stockId?: number, materialId?: number) => {
    console.debug('[RC] fetchStockColors called', { stockId, materialId });
    try {
      const token = localStorage.getItem('token')
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      if (stockId) {
        const resp = await axios.get(`${FASHION_API_URL}/stocks/${stockId}`, { headers })
        const stock = resp.data
        if (stock && stock.StockColors) {
          const result = stock.StockColors.map((sc: any) => ({
            id: sc.id,
            colorId: sc.colorId,
            stockId,
            color: { id: sc.colorId, name: sc.ItemColor?.name || `Cor ${sc.colorId}` },
          }));
          console.debug('[RC] fetchStockColors result count', result.length, 'for stockId', stockId);
          return result;
        }
      } else if (materialId) {
        const resp = await axios.get(`${FASHION_API_URL}/stocks`, { headers, params: { materialId } })
        const stocks: any[] = Array.isArray(resp.data) ? resp.data : []
        const seen: Record<number, boolean> = {}
        const colors: any[] = []
        for (const stk of stocks) {
          if (stk.StockColors) {
            stk.StockColors.forEach((sc: any) => {
              if (!seen[sc.colorId]) {
                seen[sc.colorId] = true
                colors.push({
                  id: sc.id,
                  colorId: sc.colorId,
                  stockId: stk.id,
                  color: { id: sc.colorId, name: sc.ItemColor?.name || `Cor ${sc.colorId}` },
                })
              }
            })
          }
        }
        console.debug('[RC] fetchStockColors result count', colors.length, 'for materialId', materialId);
        return colors
      }
    } catch (err) {
      console.error('Erro ao buscar cores do stock', err)
    }
    return []
  }

  const getTemplateResourceIdByMaterial = (materialId?: number | null, categoryId?: number | null): number | null => {
    if (!materialId) return null

    if (categoryId && resourcesByCategory[categoryId]) {
      const byCategory = resourcesByCategory[categoryId] || []
      const exact = byCategory.find((r) => Number(r.materialId) === Number(materialId))
      if (exact?.id) return exact.id
    }

    const allResources = Object.values(resourcesByCategory).flat()
    const match = allResources.find((r) => Number(r.materialId) === Number(materialId))
    return match?.id || null
  }

  const fetchResourceMaterialColors = async (resourceMaterialId: number) => {
    try {
      const token = localStorage.getItem('token')
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      const resp = await axios.get(`${FASHION_API_URL}/colors`, {
        headers,
        params: { resourceMaterialId },
      })
      const colors = Array.isArray(resp.data) ? resp.data : []
      return colors.map((c: any) => ({
        id: c.id,
        colorId: c.id,
        name: c.name,
        color: { id: c.id, name: c.name || `Cor ${c.id}` },
      }))
    } catch (err) {
      console.error('Erro ao buscar cores por material de recurso', err)
      return []
    }
  }

  const fetchResourceColors = async (resourceId: number) => {
    try {
      const token = localStorage.getItem('token')
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      const resp = await axios.get(`${FASHION_API_URL}/resources/${resourceId}/colors`, { headers })
      const colors = Array.isArray(resp.data) ? resp.data : []
      return colors.map((rc: any) => ({ id: rc.itemColorId, color: { id: rc.itemColorId, name: rc.ItemColor?.name || `Cor ${rc.itemColorId}` } }))
    } catch (err) {
      console.error('Erro ao buscar cores do recurso', err)
    }
    return []
  }

  const handleStockChange = async (itemId: number, stockId: number) => {
    let variations: MaterialVariation[] = []
    let stockDescription = ''
    try {
      const token = localStorage.getItem('token')
      const headers = { Authorization: `Bearer ${token}` }

      try {
        const stockRes = await axios.get(`${FASHION_API_URL}/stocks/${stockId}`, {
          headers,
        })
        if (stockRes.data) {
          stockDescription = stockRes.data.description || stockRes.data.name || `Stock ${stockId}`
        }
      } catch (stockError) {
        console.error('Erro ao carregar descrição do stock:', stockError)
        const stockInList = conjuntoStocks.find(s => s.id === stockId);
        if (stockInList) {
          stockDescription = stockInList.description || `Stock ${stockId}`;
        }
      }

      const colors = await fetchStockColors(stockId)
      variations = colors.map((c: any) => ({
        id: c.id,
        colorId: c.id,
        stockId,
        resourceId: null,
        color: { id: c.id, name: c.color.name },
        stock: null,
        resource: null,
      }))
    } catch (error) {
      console.error('Erro ao carregar variações:', error)
    }

    if (itemId && stockId) {
      try {
        const token = localStorage.getItem('token')
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const res = await axios.get(`${FASHION_API_URL}/stocks/${stockId}`, { headers })
        if (res.data && res.data.materialId) {
          setCompositionItems(prev =>
            prev.map(it =>
              it.id === itemId
                ? { ...it, materialId: res.data.materialId }
                : it
            )
          )
        }
      } catch (err) {
        console.error('erro ao buscar material a partir do stock (handleStockChange)', err)
      }
    }

    setCompositionItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
            ...item,
            stockId,
            descricao: item.tipo === 'materia-prima' ? item.descricao : stockDescription || item.descricao,
            availableVariations: variations,
            productItemColors: item.productItemColors,
          }
          : item
      )
    )
  }

  useEffect(() => {
    compositionItems.forEach(async (item) => {
      if (item.tipo === 'materia-prima' && item.stockId && item.materialId == null) {
        try {
          const token = localStorage.getItem('token')
          const headers = token ? { Authorization: `Bearer ${token}` } : {}
          const res = await axios.get(`${FASHION_API_URL}/stocks/${item.stockId}`, { headers })
          const mat = res.data?.materialId
          if (mat) {
            console.debug('[RC] povoando materialId a partir do stock (effect)', { itemId: item.id, stockId: item.stockId, materialId: mat })
            setCompositionItems(prev =>
              prev.map(it =>
                it.id === item.id ? { ...it, materialId: mat } : it
              )
            )
          }
        } catch (err) {
          console.error('erro ao buscar material a partir do stock (effect)', err)
        }
      }
    })
  }, [compositionItems])

  const handleUnidadeMedidaChange = (itemId: number, unidadeId: number) => {
    const item = compositionItems.find((i) => i.id === itemId)
    if (!item) return

    const selectedUnit = units.find(u => u.id === unidadeId)
    const unidade = selectedUnit?.name || ''
    console.debug('[RC] alteração unidade item', itemId, 'de', item.unitOfMeasurementId, 'para', unidadeId, 'cores anteriores', item.productItemColors)

    setCompositionItems((prev) => {
      const updated = prev.map((i) =>
        i.id === itemId ? { ...i, unidadeMedida: unidade, unitOfMeasurementId: unidadeId } : i
      )
      const updatedItem = updated.find(i => i.id === itemId)
      console.debug('[RC] item após mudança unidade', updatedItem)
      console.debug('[RC] compositionItems after unidade change', updated)
      return updated
    })
  }

  const handleCompositionFieldChange = (
    itemId: number,
    field: keyof CompositionItem,
    value: string
  ) => {
    setCompositionItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, [field]: value } : item))
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.nomeProduto.trim()) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Por favor, preencha o nome do produto.',
      })
      return
    }

    if (!formData.collectionId) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Por favor, selecione uma coleção.',
      })
      return
    }

    if (selectedGradeIds.length === 0) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'É obrigatório selecionar pelo menos uma grade.',
      })
      return
    }

    setLoading(true)

    let attachmentsToSend = [...productAttachmentUrls]
    if (productSelectedFiles.length > 0) {
      try {
        const uploaded = await uploadProductAttachmentFiles(productSelectedFiles)
        attachmentsToSend = [...attachmentsToSend, ...uploaded]
        setProductAttachmentUrls(attachmentsToSend)
        setProductSelectedFiles([])
      } catch (uploadError) {
        console.error('Erro ao enviar anexos:', uploadError)
        setLoading(false)
        setFeedback({
          isOpen: true,
          statusOk: false,
          message: 'Erro ao enviar anexos. Tente novamente.',
        })
        return
      }
    }

    const currentCoverAttachment = productExistingAttachments.find((attachment) => attachment.isCover)
    const coverAttachmentUrl = currentCoverAttachment
      ? currentCoverAttachment.fileUrl || currentCoverAttachment.filePath || null
      : null

    const payload = {
      productCode: formData.codigo,
      name: formData.nomeProduto,
      price: formData.price || undefined,
      description: formData.description || undefined,
      referenceProduct: formData.referenceProduct || undefined,
      observations: formData.observations || undefined,
      collectionId: formData.collectionId,
      familyId: formData.familyId,
      groupId: formData.groupId || undefined,
      ...(isEditMode
        ? { episodeId: formData.episodeId > 0 ? formData.episodeId : null }
        : formData.episodeId > 0
          ? { episodeId: formData.episodeId }
          : {}),
      gradeIds: selectedGradeIds,
      attachments: attachmentsToSend,
      coverAttachmentUrl,
      compositionItems: compositionItems
        .filter((item) => item.tipo === 'materia-prima' || item.tipo === 'insumo' || item.tipo === 'servico')
        .map((item) => {
          const base: any = {
            tipo: item.tipo,
            usage: item.consumo ? Number(String(item.consumo).replace(',', '.')) : undefined,
            unitOfMeasurementId: item.unitOfMeasurementId || undefined,
          }
          const rawCost = item.costPerUnit?.trim()
          if (rawCost) {
            const n = Number(String(rawCost).replace(',', '.'))
            if (!Number.isNaN(n)) {
              base.costPerUnit = n
            }
          }

          if (item.tipo === 'materia-prima') {
            return {
              ...base,
              materialId: item.materialId || undefined,
              stockId: item.stockId || undefined,
            }
          }

          if (item.tipo === 'insumo') {
            return {
              ...base,
              resourceId: item.resourceId || undefined,
              materialId: item.materialId || undefined,
            }
          }

          return {
            ...base,
            stageId: item.stageId || undefined,
            deliveryTime: item.consumo || undefined,
          }
        }),

      productItemColors: (() => {
        const serverProductColors: any[] =
          (productData as any)?.ProductItemColor ||
          (productData as any)?.productItemColors ||
          []

        const fromCoresSection = selectedCollectionColors.map(col => {
          const existing =
            serverProductColors.find(
              (p: any) =>
                p.collectionColorId === col.id && !p.stockId && !p.resourceId,
            ) || serverProductColors.find((p: any) => p.collectionColorId === col.id)

          return {
            ...(existing?.id != null ? { id: existing.id } : {}),
            collectionColorId: col.id,
          }
        })

        const fromComposition = compositionItems.flatMap(item =>
          (item.productItemColors || [])
            .filter(p => p.itemColorId)
            .map(p => {
              const base: any = {
                id: p.id,
                collectionColorId: p.collectionColorId,
                itemColorId: p.itemColorId,
              }

              if (item.materialId) {
                base.materialId = item.materialId
              }
              if (p.resourceId) {
                base.resourceId = p.resourceId
              } else if (item.resourceId) {
                base.resourceId = item.resourceId
              }
              if (item.stockId) {
                base.stockId = item.stockId
              }

              return base
            }),
        )

        const productItemColorKey = (entry: any) => {
          if (entry.id != null) return `id:${entry.id}`
          if (
            entry.itemColorId ||
            entry.stockId ||
            entry.resourceId ||
            entry.materialId
          ) {
            return `comp:${entry.collectionColorId}_${entry.itemColorId || ''}_${entry.stockId || ''}_${entry.resourceId || ''}_${entry.materialId || ''}`
          }
          return `tag:${entry.collectionColorId}`
        }

        const unique = new Map<string, any>()
        const mergeEntry = (entry: any) => {
          const key = productItemColorKey(entry)
          const existing = unique.get(key)
          if (!existing || (existing.id == null && entry.id != null)) {
            unique.set(key, entry)
          }
        }

        fromCoresSection.forEach(mergeEntry)
        fromComposition.forEach(mergeEntry)

        return Array.from(unique.values())
      })(),
      beneficiamentoNotes: [] as any[],
    }

    try {
      const resolvedNotes = await Promise.all(
        beneficiamentoNotes.map(async (nota) => {
          let uploadedUrls: string[] = []
          if (nota.imageFiles.length > 0) {
            uploadedUrls = await Promise.all(nota.imageFiles.map(f => uploadNoteImage(f)))
          }
          return {
            sectorId: nota.sectorId,
            description: nota.description || undefined,
            imageUrls: [...nota.imageOriginalUrls, ...uploadedUrls],
          }
        })
      )
      payload.beneficiamentoNotes = resolvedNotes
    } catch (noteUploadError) {
      console.error('Erro ao enviar imagens de notas de beneficiamento:', noteUploadError)
      setLoading(false)
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Erro ao enviar imagens das notas. Tente novamente.',
      })
      return
    }

    try {
      const token = localStorage.getItem('token')
      console.log('[RegisterComposition] payload', JSON.stringify(payload, null, 2));
      console.log('[RegisterComposition] payload compositionItems', payload.compositionItems);
      console.log('[RegisterComposition] compositionItems (raw)', JSON.stringify(compositionItems, null, 2));
      console.log('[RegisterComposition] compositionItems payload mapping', payload.compositionItems.map((i: any) => ({
        tipo: i.tipo,
        materialId: i.materialId,
        resourceId: i.resourceId,
        stockId: i.stockId,
        stageId: i.stageId,
        deliveryTime: i.deliveryTime,
        unitOfMeasurementId: i.unitOfMeasurementId,
      })));
      console.log('[RegisterComposition] payload colors', payload.productItemColors);

      if (isEditMode && productId) {
        await axios.put(`${FASHION_API_URL}/${productId}`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })

        await measuresRef.current?.save(Number(productId))

        setFeedback({
          isOpen: true,
          statusOk: true,
          message: 'Produto atualizado com sucesso!',
        })
      } else {
        const createResp = await axios.post(`${FASHION_API_URL}`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })

        const createdId = createResp.data?.id
        if (createdId) {
          await measuresRef.current?.save(createdId)
        }

        setFeedback({
          isOpen: true,
          statusOk: true,
          message: 'Produto cadastrado com sucesso!',
        })
      }

      navigate(returnTo || '/panel/composition-product')
    } catch (err: any) {
      console.error('Erro ao salvar produto:', err, err.response?.data)
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: err.response?.data?.message || 'Erro ao salvar produto. Tente novamente.',
      })
    } finally {
      setLoading(false)
    }
  }

  const breadcrumbItems = [
    { label: 'Ficha técnica de produto', path: '/panel/composition-product' },
    { label: isEditMode ? 'Editar produto' : 'Cadastrar produto' },
  ]

  const hasMaterialItem = React.useMemo(
    () => compositionItems.some((i) => i.tipo === 'materia-prima'),
    [compositionItems],
  )

  const isFormValid = formData.nomeProduto.trim() !== '' && formData.collectionId > 0

  const displayGrades = React.useMemo(() => {
    if (!selectedGradeType) return []

    const gradeType = gradeTypes.find(t => t.id === selectedGradeType)
    if (!gradeType) return []

    const gradesOfType = gradesByType[gradeType.name] || []
    return gradesOfType.filter(grade => availableGradeIds.includes(grade.id))
  }, [selectedGradeType, gradeTypes, gradesByType, availableGradeIds])

  if (loadingData || (isEditMode && isInitializing)) {
    return (
      <MainContainer>
        <p>Carregando dados...</p>
      </MainContainer>
    )
  }

  return (
    <>
      <SubHeaderContainer style={{ margin: '0 -1.2rem 0' }}>
        <LeftSide>
          <SectionName>{isEditMode ? 'Editar ficha técnica de produto' : 'Cadastrar ficha técnica de produto'}</SectionName>
        </LeftSide>
        <ActionButtons>
          <ImportButton type='button'>
            <DownloadIcon />
            Importar produto base
          </ImportButton>
          <CancelButton type='button' onClick={() => navigate(returnTo || '/panel/composition-product')}>
            Cancelar
          </CancelButton>
          <SaveButton type='button' onClick={handleSubmit} disabled={!isFormValid || loading}>
            <img src={save} alt='Salvar' />
            {loading ? 'Salvando...' : (isEditMode ? 'Atualizar produto' : 'Salvar produto')}
          </SaveButton>
        </ActionButtons>
      </SubHeaderContainer>

      <Breadcrumbs items={breadcrumbItems} />

      <MainContainer>

        <form onSubmit={handleSubmit}>
          <PageGrid $asideOpen={isAttachmentsAsideOpen}>
            <FormColumn>
          <StyledCard>
            <CollapsePanelHeader
              $open={isGeralOpen}
              onClick={() => setIsGeralOpen((prev) => !prev)}
            >
              <CardTitle>Geral</CardTitle>
              {isGeralOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </CollapsePanelHeader>
            {isGeralOpen && <FormGrid cols={2}>
              <StyledFormGroup>
                <label htmlFor='codigo'>Código do produto</label>
                <input
                  type='text'
                  id='codigo'
                  name='codigo'
                  value={formData.codigo}
                  onChange={handleChange}
                  disabled={isEditMode}
                />
              </StyledFormGroup>
              <StyledFormGroup>
                <label htmlFor='nomeProduto'>Nome do produto</label>
                <input
                  type='text'
                  id='nomeProduto'
                  name='nomeProduto'
                  value={formData.nomeProduto}
                  onChange={handleChange}
                />
              </StyledFormGroup>
              <StyledFormGroup>
                <label htmlFor='description'>Descrição</label>
                <input
                  type='text'
                  id='description'
                  name='description'
                  value={formData.description}
                  onChange={handleChange}
                />
              </StyledFormGroup>
              <StyledFormGroup>
                <label htmlFor='referenceProduct'>Produto referência</label>
                <input
                  type='text'
                  id='referenceProduct'
                  name='referenceProduct'
                  value={formData.referenceProduct}
                  onChange={handleChange}
                />
              </StyledFormGroup>
              <StyledFormGroup>
                <label htmlFor='price'>Valor (preço do produto)</label>
                <CurrencyInput
                  id='price'
                  name='price'
                  value={formData.price}
                  onValueChange={(_value, _name, values) => {
                    setFormData(prev => ({ ...prev, price: values?.float ?? 0 }))
                  }}
                  placeholder='R$ 0,00'
                />
              </StyledFormGroup>
              <StyledFormGroup>
                <label htmlFor='collectionId'>Coleção vinculada</label>
                <select
                  id='collectionId'
                  name='collectionId'
                  value={formData.collectionId || ''}
                  onChange={handleChange}
                  disabled={isEditMode}
                >
                  <option value=''>Selecione</option>
                  {allCollections.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </StyledFormGroup>

              <StyledFormGroup>
                <label htmlFor='familyId'>Família</label>
                <select
                  id='familyId'
                  name='familyId'
                  value={formData.familyId || ''}
                  onChange={handleChange}
                  disabled={!formData.collectionId}
                >
                  <option value=''>Selecione</option>
                  {filteredFamilies.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </StyledFormGroup>
              <StyledFormGroup>
                <label htmlFor='episodeId'>Episódio</label>
                <select
                  id='episodeId'
                  name='episodeId'
                  value={formData.episodeId || ''}
                  onChange={handleChange}
                  disabled={!formData.collectionId}
                >
                  <option value=''>Nenhum</option>
                  {collectionEpisodes.map((ep) => (
                    <option key={ep.id} value={ep.id}>
                      {ep.name}
                    </option>
                  ))}
                </select>
              </StyledFormGroup>
              <CollectionStyles.StyledFormGroup>
                <label htmlFor='groupId' style={{ minHeight: '16px', display: 'block' }}>Grupo</label>
                <select
                  id='groupId'
                  name='groupId'
                  value={formData.groupId || ''}
                  onChange={(e) => handleChange(e)}
                  disabled={!formData.collectionId}
                >
                  <option value=''>Selecione</option>
                  {filteredGroups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>

              </CollectionStyles.StyledFormGroup>



              <StyledFormGroup>
                <label htmlFor='gradeTypes'>Tipo de graduação</label>
                <select
                  value={selectedGradeType || ''}
                  onChange={handleGradeTypeChange}
                  disabled={!formData.collectionId || availableGradeIds.length === 0}
                >
                  <option value=''>Selecione</option>
                  {gradeTypes
                    .filter(type => {
                      const gradesOfType = gradesByType[type.name] || []
                      return gradesOfType.some(g => availableGradeIds.includes(g.id))
                    })
                    .map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                </select>
              </StyledFormGroup>

              {displayGrades.length > 0 && (
                <StyledFormGroup>
                  <label>Graduação</label>
                  <GraduationGrid>
                    {displayGrades.map((grade) => (
                      <GraduationButton
                        key={grade.id}
                        type='button'
                        $isSelected={selectedGradeIds.includes(grade.id)}
                        onClick={() => handleGradeToggle(grade.id)}
                      >
                        {grade.name}
                      </GraduationButton>
                    ))}
                  </GraduationGrid>
                </StyledFormGroup>
              )}

              <StyledFormGroup>
                <label htmlFor='observations'>Observações</label>
                <textarea
                  id='observations'
                  name='observations'
                  rows={4}
                  value={formData.observations}
                  onChange={handleChange}
                />
              </StyledFormGroup>

            </FormGrid>}
          </StyledCard>

          <Colors
            allColors={allCollectionColors}
            selectedColors={selectedCollectionColors}
            onAdd={(col) => {
              if (!selectedCollectionColors.some(sc => sc.id === col.id)) {
                setSelectedCollectionColors(prev => [...prev, col])
              }
            }}
            onRemove={removeCollectionColor}
            disabled={!formData.collectionId || allCollectionColors.length === 0}
          />

          <StyledCard id="composition-section">
            <CollapsePanelHeader
              $open={isComposicaoOpen}
              onClick={() => setIsComposicaoOpen((prev) => !prev)}
            >
              <CardTitle>Composição</CardTitle>
              {isComposicaoOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </CollapsePanelHeader>
            {isComposicaoOpen && <>
            {compositionItems.length > 0 && (
              <>
                <CompositionTableWrapper>
                  <CompositionTable>
                    <CompositionTableHeader>
                      <span>Tipo</span>
                      <span>Material/Insumo</span>
                      <span>Acabamento</span>
                      <span>Consumo</span>
                      <span>Un. de medida</span>
                      <span>Custo / un.</span>
                      {selectedCollectionColors.map(col => (
                        <span className='color-column' key={`col-header-${col.id}`}>{col.name}</span>
                      ))}
                    </CompositionTableHeader>

                    {compositionItems.map((item, index) => {
                      return (
                        <CompositionTableRow key={item.id}>
                          <TableCell $sticky='first'>
                            <TableSelect
                              value={item.tipo || ''}
                              onChange={(e) => handleCompositionTypeChange(item.id, e.target.value as 'materia-prima' | 'insumo' | 'servico' | '')}
                            >
                              <option value=''>Tipo</option>
                              <option value='materia-prima'>
                                Material
                              </option>
                              <option value='insumo'>Insumo</option>
                              <option value='servico'>Serviço</option>
                            </TableSelect>
                          </TableCell>

                          <TableCell $sticky='second'>
                            {item.tipo === 'materia-prima' ? (
                              <TableSelect
                                value={
                                  (familyConjuntos.find(c =>
                                    (item.stockId && c.stockId === item.stockId) ||
                                    (!item.stockId && c.materialId === item.materialId)
                                  )?.familyMaterialId) || ''
                                }
                                onChange={(e) => handleMaterialSelect(item.id, Number(e.target.value))}
                              >
                                <option value=''>Selecione material</option>
                                {(() => {
                                  const occupiedByOtherRows = new Set(
                                    compositionItems
                                      .filter((ci) => ci.id !== item.id && ci.tipo === 'materia-prima')
                                      .map((ci) => materiaPrimaFamilyMaterialIdFromItem(ci, familyConjuntos))
                                      .filter((v): v is number => v != null),
                                  )

                                  const currentVal = materiaPrimaFamilyMaterialIdFromItem(item, familyConjuntos)

                                  return familyConjuntos
                                    .filter((c) => {
                                      if (currentVal != null && c.familyMaterialId === currentVal) return true
                                      return !occupiedByOtherRows.has(c.familyMaterialId)
                                    })
                                    .map((c) => (
                                      <option key={c.familyMaterialId} value={c.familyMaterialId}>
                                        {c.stockLabel || c.materialName}
                                      </option>
                                    ))
                                })()}
                              </TableSelect>
                            ) : item.tipo === 'insumo' ? (
                              <TableSelect
                                value={insumoOptionValueFromItem(item) ?? ''}
                                onChange={(e) => handleInsumoGroupSelect(item.id, e.target.value || null)}
                              >
                                <option value=''>Selecione material</option>
                                {(() => {
                                  const occupiedByOtherRows = new Set(
                                    compositionItems
                                      .filter((ci) => ci.id !== item.id && ci.tipo === 'insumo')
                                      .map((ci) => insumoOptionValueFromItem(ci))
                                      .filter((v): v is string => v != null),
                                  )

                                  const currentVal = insumoOptionValueFromItem(item)

                                  const options = insumoMaterialGroups
                                    .filter((g) => {
                                      const gv = insumoOptionValueFromGroup(g)
                                      if (gv == null) return false
                                      if (currentVal != null && currentVal === gv) return true
                                      return !occupiedByOtherRows.has(gv)
                                    })
                                    .map((g) => ({
                                      key:
                                        g.resourceId != null
                                          ? `r-${g.resourceId}`
                                          : `${g.materialId ?? 'sem-material'}-${g.categoryId ?? 'sem-categoria'}`,
                                      value:
                                        g.resourceId != null
                                          ? `r:${g.resourceId}`
                                          : `${g.materialId ?? ''}:${g.categoryId ?? ''}`,
                                      name: g.materialName,
                                    }))

                                  return options.map((opt) => (
                                    <option key={opt.key} value={opt.value}>
                                      {opt.name}
                                    </option>
                                  ))
                                })()}
                              </TableSelect>
                            ) : item.tipo === 'servico' ? (
                              <TableSelect
                                value={item.stageId || ''}
                                onChange={(e) => handleServiceSelect(item.id, Number(e.target.value))}
                              >
                                <option value=''>Selecione serviço</option>
                                {(() => {
                                  const occupiedStageIds = new Set(
                                    compositionItems
                                      .filter((ci) => ci.id !== item.id && ci.tipo === 'servico')
                                      .map((ci) => servicoStageIdFromItem(ci))
                                      .filter((v): v is number => v != null),
                                  )

                                  const currentStageId = servicoStageIdFromItem(item)

                                  return allServices
                                    .filter((s) => {
                                      if (currentStageId != null && s.id === currentStageId) return true
                                      return !occupiedStageIds.has(s.id)
                                    })
                                    .map((s) => (
                                      <option key={s.id} value={s.id}>
                                        {s.name}
                                      </option>
                                    ))
                                })()}
                              </TableSelect>
                            ) : (
                              <span
                                style={{
                                  display: 'block',
                                  padding: '12px 14px',
                                  fontSize: 13,
                                  color: '#9ca3af',
                                }}
                              >
                                N/D
                              </span>
                            )}
                          </TableCell>

                          <TableCell>
                            <span
                              style={{
                                display: 'block',
                                padding: '12px 14px',
                                fontSize: 13,
                                color: '#374151',
                              }}
                            >
                              {item.tipo === 'materia-prima' || item.tipo === 'insumo' ? (item.finishing || '—') : '—'}
                            </span>
                          </TableCell>

                          <TableCell>
                            <TableInput
                              type='text'
                              inputMode='decimal'
                              autoComplete='off'
                              value={item.consumo}
                              onChange={(e) => {
                                handleCompositionFieldChange(
                                  item.id,
                                  'consumo',
                                  sanitizeCompositionDecimalInput(e.target.value),
                                )
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            <TableSelect
                              value={item.unitOfMeasurementId || ''}
                              onChange={(e) => handleUnidadeMedidaChange(item.id, Number(e.target.value))}
                            >
                              <option value=''></option>
                              {(() => {
                                const TIME_UNITS = ['minuto', 'minutos', 'hora', 'horas', 'dia', 'dias']
                                const filtered =
                                  item.tipo === 'servico'
                                    ? units.filter(u => TIME_UNITS.includes(String(u.name || '').trim().toLowerCase()))
                                    : units.filter(u => !TIME_UNITS.includes(String(u.name || '').trim().toLowerCase()))
                                return filtered
                              })().map((u) => (
                                <option key={u.id} value={u.id}>
                                  {u.name}
                                </option>
                              ))}
                            </TableSelect>
                          </TableCell>

                          <TableCell>
                            <TableInput
                              type='text'
                              inputMode='decimal'
                              autoComplete='off'
                              value={item.costPerUnit ?? ''}
                              onChange={(e) => {
                                handleCompositionFieldChange(
                                  item.id,
                                  'costPerUnit',
                                  sanitizeCompositionDecimalInput(e.target.value),
                                )
                              }}
                            />
                          </TableCell>

                          {selectedCollectionColors.map(col => {
                            if (item.tipo === 'servico') {
                              return <TableCell className='color-column' key={`color-cell-${col.id}`} />
                            }
                            const existing = item.productItemColors?.find(p => p.collectionColorId === col.id);
                            const isMaterial = item.tipo === 'materia-prima';
                            const params: any = {};
                            if (isMaterial) {
                              const baseMaterial = item.materialId;
                              if (baseMaterial != null) {
                                params.materialId = baseMaterial;
                              } else if (item.stockId != null) {
                                params.stockId = item.stockId;
                              }
                            } else {
                              if (item.materialId != null) {
                                params.resourceMaterialId = item.materialId;
                              }
                            }

                            const hasParams = params && Object.keys(params).length > 0;

                            const templateResourceId = item.materialId ? getTemplateResourceIdByMaterial(item.materialId, item.categoryId) : null;

                            const dynamicEndpoint = {
                              baseUrl: FASHION_API_URL,
                              listPath: '/colors',
                              createPath: '/colors',
                              deletePath: '/colors',
                              searchParam: 'search',
                              additionalParams: hasParams ? params : undefined,
                            };

                            let currentColorId: number | null = null;
                            if (existing?.itemColorId) currentColorId = existing.itemColorId;
                            else if (item.resourceItemColorId && selectedCollectionColors.length === 1) currentColorId = item.resourceItemColorId;

                            const getColorName = (id: number) => {
                              const varMatch = allItemColors.find(ai => ai.id === id) ||
                                allCollectionColors.find(ac => ac.id === id);
                              if (varMatch) return varMatch.name;
                              if (item.resourceItemColorId === id && item.resourceItemColorName) return item.resourceItemColorName;
                              const variation = allVariations?.find(v => v.colorId === id || v.id === id);
                              if (variation) return variation.color?.name || `Cor ${id}`;
                              return `Cor ${id}`;
                            };

                            const handleCreateColor = async (name: string) => {
                              const token = localStorage.getItem('token');
                              const headers = token ? { Authorization: `Bearer ${token}` } : {};
                              const createRes = await axios.post(`${FASHION_API_URL}/colors`, { name }, { headers });
                              const newColor = createRes.data;
                              if (isMaterial && item.materialId) {
                                try {
                                  await axios.post(
                                    `${FASHION_API_URL}/materials/${item.materialId}/stock-colors`,
                                    { colorId: newColor.id },
                                    { headers }
                                  );
                                } catch (err) {
                                  console.error('erro ao associar cor a estoque/material', err);
                                }
                              }
                              if (!isMaterial && item.materialId) {
                                try {
                                  const templateResourceId = getTemplateResourceIdByMaterial(item.materialId, item.categoryId)
                                  if (templateResourceId) {
                                    const resp = await axios.post(
                                      `${FASHION_API_URL}/resources/${templateResourceId}/resource-colors`,
                                      { colorId: newColor.id },
                                      { headers }
                                    );

                                    const newResourceId = resp.data?.resourceId ?? templateResourceId;
                                    setCompositionItems(prev => prev.map(it => {
                                      if (it.id !== item.id) return it;
                                      const updatedProductItemColors = (it.productItemColors || []).map(p =>
                                        p.collectionColorId === col.id ? { ...p, resourceId: newResourceId } : p
                                      );
                                      return { ...it, productItemColors: updatedProductItemColors };
                                    }));
                                  }
                                } catch (err) {
                                  console.error('erro ao associar cor a recurso/material', err);
                                }
                              }

                              return { id: newColor.id, name: newColor.name };
                            };

                            if (!item.materialId && !item.stockId) {
                              return (
                                <TableCell className='color-column' key={`color-cell-${col.id}`}>
                                  <TableSelect disabled value=''>
                                    <option value=''>Selecione material</option>
                                  </TableSelect>
                                </TableCell>
                              );
                            } else {
                              return (
                                <TableCell className='color-column' key={`color-cell-${col.id}`}>
                                  <SelectContainer $flush>
                                    <InfiniteSelect
                                      triggerVariant='cell'
                                      key={`${col.id}-${JSON.stringify(params)}`}
                                      placeholder='Selecione cor do item'
                                      mode='crud'
                                      showSelectedBelow={false}
                                      endpointConfig={dynamicEndpoint}
                                      disabled={!hasParams}
                                      initialOptions={currentColorId ? [{ id: currentColorId, name: getColorName(currentColorId) }] : []}
                                      value={currentColorId ? { id: currentColorId, name: getColorName(currentColorId) } : null}
                                      onItemCreate={item.materialId ? handleCreateColor : undefined}
                                      dropdownForceFixed={true}
                                      dropdownZIndex={2147483600 + index * 10}
                                      onSelect={async (option: any) => {
                                        const token = localStorage.getItem('token');

                                        if (option && option.id != null && option.name) {
                                          setAllItemColors(prev => {
                                            const exists = prev.some(c => String(c.id) === String(option.id));
                                            if (!exists) {
                                              return [...prev, { id: option.id, name: option.name }];
                                            }
                                            return prev;
                                          });
                                        }
                                        const colorVal = option ? Number(option.id) : null;
                                        let stockColorVal: number | undefined;
                                        let entryResourceId: number | undefined;

                                        if (colorVal != null && (item.materialId || item.stockId)) {
                                          try {
                                            const params: any = {};
                                            if (item.materialId) params.materialId = item.materialId;
                                            if (item.stockId) params.stockId = item.stockId;

                                            if (isMaterial) {
                                              const list = await fetchStockColors(item.stockId || undefined, item.materialId || undefined);
                                              const match = list.find((v: any) => v.colorId === colorVal);
                                              if (match) stockColorVal = match.id;
                                            } else if (templateResourceId) {

                                              try {
                                                const resp = await axios.post(
                                                  `${FASHION_API_URL}/resources/${templateResourceId}/resource-colors`,
                                                  { colorId: colorVal },
                                                  { headers: token ? { Authorization: `Bearer ${token}` } : {} }
                                                );
                                                entryResourceId = resp.data?.resourceId;
                                              } catch (err) {
                                                console.error('erro ao criar recurso para cor do insumo', err);
                                              }
                                            }
                                          } catch (e) {
                                            console.error('erro ao determinar stockColorId para cor selecionada', e);
                                          }
                                        }

                                        setCompositionItems(prev => prev.map(it => {
                                          if (it.id !== item.id) return it;
                                          const updated: ProductItemColorEntry[] = (it.productItemColors || [])
                                            .filter(p => p.collectionColorId !== col.id);
                                          if (colorVal !== null) {
                                            const entry: ProductItemColorEntry = { collectionColorId: col.id, itemColorId: colorVal };
                                            if (entryResourceId) entry.resourceId = entryResourceId;
                                            updated.push(entry);
                                          }
                                          return { ...it, productItemColors: updated };
                                        }));
                                      }}

                                      onItemDelete={async (id: any) => {
                                        setCompositionItems(prev => prev.map(it => {
                                          const updated = (it.productItemColors || []).map(p => ({ ...p }));
                                          for (let i = updated.length - 1; i >= 0; i--) {
                                            if (String(updated[i].itemColorId) === String(id)) {
                                              updated[i] = { ...updated[i], itemColorId: null, itemColorName: null };
                                            }
                                          }
                                          return { ...it, productItemColors: updated };
                                        }));
                                      }}
                                      getOptionLabel={(opt: any) => opt.name
                                        || (allItemColors.find(ai => String(ai.id) === String(opt.id))?.name)
                                        || (allCollectionColors.find(ac => String(ac.id) === String(opt.id))?.name)
                                        || `Cor ${opt.id}`}
                                    />
                                  </SelectContainer>
                                </TableCell>
                              );
                            }
                          })}

                          <TableCell>
                            <DeleteButton type='button' onClick={() => removeCompositionItem(item.id)}>
                              <img src={trash} alt='Excluir' />
                            </DeleteButton>
                          </TableCell>
                        </CompositionTableRow>
                      );
                    })}
                  </CompositionTable>
                </CompositionTableWrapper>


              </>
            )}

            <AddItemButton type='button' onClick={addCompositionItem}>
              <img src={plus} alt='Adicionar' />
              Adicionar item
            </AddItemButton>
            </>}
          </StyledCard>

          <Beneficiamento
            title="Anotações"
            notes={beneficiamentoNotes}
            onChange={setBeneficiamentoNotes}
            sectors={sectors}
            isOpen={isBeneficiamentoOpen}
            onToggle={() => setIsBeneficiamentoOpen(prev => !prev)}
          />
          <Aprovations productId={isEditMode ? Number(productId) : undefined} />
          <Measures
            ref={measuresRef}
            productId={Number(productId)}
            groupId={formData.groupId}
            selectedGrades={selectedGradeIds
              .map((id: number) => Object.values(gradesByType).flat().find((g) => g.id === id))
              .filter(Boolean) as { id: number; name: string }[]}
          />
            </FormColumn>
            <AsideColumn>
              <ProductAttachmentsAside
                selectedFiles={productSelectedFiles}
                existingAttachments={productExistingAttachments}
                onFileChange={handleProductAttachmentChange}
                onRemoveFile={handleProductAttachmentRemove}
                onViewAttachment={handleViewProductExistingAttachment}
                onDownloadAttachment={handleDownloadProductExistingAttachment}
                onRemoveExistingAttachment={handleProductExistingAttachmentRemove}
                onSetCoverAttachment={handleSetProductAttachmentCover}
                onRemoveCoverAttachment={handleRemoveProductAttachmentCover}
                formatFileSize={(bytes) => `${(bytes / 1024).toFixed(1)} KB`}
                isOpen={isAttachmentsAsideOpen}
                onToggleOpen={(next) => setIsAttachmentsAsideOpen(next)}
              />
            </AsideColumn>
          </PageGrid>
        </form>
      </MainContainer>
    </>
  )
}

export default RegisterComposition